// Workflows — the production board (one level up from Projects). A React port of
// the design prototype, wired to the server-side Workflow entities via
// useWorkflowBoard(). Superadmin-gated for now (see /api/me → entitlements).
//
// All classNames are scoped under `.wf-board` and prefixed `wf-` so nothing
// collides with the app's global styles (which also use .row/.cell/.menu/etc).

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactElement } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, downloadFile } from "../api/client";
import { useMe } from "../lib/me";
import { anchored } from "../lib/anchor";
import { useBoardViews, useWorkflowBoard, useWorkflowFolders } from "../lib/workflowBoard";
import type { BoardView, Project, Workflow, WorkflowBatch, WorkflowFolder, WorkflowPatch } from "../api/types";
import { Icon } from "../components/icons";
import { toast } from "../components/toast";
import { DuplicateProjectDialog } from "../components/DuplicateProjectDialog";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { TrackingView } from "../components/TrackingView";
import { AssignProoferPanel } from "../components/AssignProofer";
import { useTracking } from "../lib/tracking";
import { avatarColor, initials } from "../lib/avatar";
import { LABELS, labelDot, labelName } from "../lib/labels";
import { today, useDayTick } from "../lib/today";
import { TimelineView } from "../components/TimelineView";
import type { GroupBy as TlGroupBy } from "../components/TimelineView";
import { backdropClose } from "../lib/backdrop";

// ---------------------------------------------------------------------------
// Status lifecycle
// ---------------------------------------------------------------------------
type StatusKey =
  | "announced" | "pending_chain" | "assigning" | "in_progress"
  | "confirm" | "on_hold" | "complete";

const STATUS: Record<StatusKey, { label: string; dot: string; bar: string; bg: string; fg: string; hint: string }> = {
  in_progress:   { label: "In Progress",   dot: "#2540F6", bar: "#2540F6", bg: "#E6ECFE", fg: "#2540F6", hint: "Being worked on." },
  assigning:     { label: "Assigning",     dot: "#8312E0", bar: "#8312E0", bg: "#F3ECFD", fg: "#8312E0", hint: "About to begin — staffing the roles." },
  confirm:       { label: "Confirm",       dot: "#E0890A", bar: "#E0890A", bg: "#FBEDD5", fg: "#A9690A", hint: "Need to clarify some aspects with the client." },
  on_hold:       { label: "On Hold",       dot: "#E23054", bar: "#E23054", bg: "#FCE7EC", fg: "#C42A48", hint: "Client is figuring things out before we start / resume." },
  pending_chain: { label: "Pending Chain", dot: "#6B72D4", bar: "#6B72D4", bg: "#E7E8FB", fg: "#484FB0", hint: "Waiting on the previous production stage before it's handed to us." },
  announced:     { label: "Announced",     dot: "#8A90AC", bar: "#B7BCD0", bg: "#E9EBF1", fg: "#666C8C", hint: "On the radar — coming our way soon." },
  complete:      { label: "Complete",      dot: "#18A97A", bar: "#18A97A", bg: "#DEF3EA", fg: "#12855F", hint: "Delivered." },
};
const STATUS_ORDER: StatusKey[] = ["in_progress", "assigning", "confirm", "on_hold", "pending_chain", "announced", "complete"];
const sMeta = (s: string) => STATUS[s as StatusKey] ?? STATUS.announced;

// A sensible board status for a project pulled in from the Projects list.
function projectToStatus(p: Project): StatusKey {
  if (p.status === "proofed") return "complete";
  if (p.proofing_active || p.status === "proofing") return "in_progress";
  if (p.n_audio > 0 || p.has_mapping) return "in_progress";
  return "assigning";  // exists but no work yet — about to begin
}
const PROJ_STATUS_LABEL: Record<string, string> = {
  created: "New", uploaded: "Uploaded", mapping_proposed: "Mapping", mapping_confirmed: "Mapped",
  proofing: "Proofing", proofed: "Proofed", error: "Error",
};

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
const pad2 = (n: number) => String(n).padStart(2, "0");
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MS = 86400000;


function parseD(s: string): Date { const p = s.split("-"); return new Date(+p[0], +p[1] - 1, +p[2]); }
function ymd(d: Date): string { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
function fmtDMY(s?: string | null): string { if (!s) return "—"; const d = parseD(s); return `${pad2(d.getDate())}-${MON[d.getMonth()]}-${d.getFullYear()}`; }
function fmtShort(s?: string | null): string { if (!s) return "—"; return parseD(s).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
function fmtRt(sec: number | null): string | null {
  if (!sec) return null;
  sec = Math.round(sec); // whole seconds — a measured runtime is fractional
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), x = sec % 60;
  return `${h}:${pad2(m)}:${pad2(x)}`;
}
function sumRt(w: Workflow): number | null {
  // Round to whole seconds: a linked project's measured runtime is a float
  // (summed file durations), and HH/MM/SS/HH:MM:SS must not show its decimals.
  const t = (w.batches && w.batches.length)
    ? w.batches.reduce((a, b) => a + (b.rt || 0), 0)
    // A hand-set runtime wins; otherwise fall back to the linked project's
    // measured runtime so a linked row shows HH/MM/SS without anyone typing it.
    : (w.runtime_seconds || w.project_runtime_s || 0);
  return Math.round(t) || null;
}
// Where a row's shown runtime comes from — drives the source glyph on HH:MM:SS.
// null when there's nothing to show or it's derived from batch sub-rows.
function rtSource(w: Workflow): "manual" | "linked" | null {
  if (w.batches && w.batches.length) return null;
  if (w.runtime_seconds) return "manual";
  if (w.project_runtime_s) return "linked";
  return null;
}
/** The runtime the audio files themselves measure, or null when there's nothing
 *  measured to fall back to (an unlinked row, or a project with no files yet). */
function measuredRt(w: Workflow): number | null {
  return w.project_runtime_s ? Math.round(w.project_runtime_s) : null;
}

/** Where the number on screen came from, and the way back to the measured one.
 *
 *  A hand-typed runtime masks the measured one silently — same digits, same
 *  cell, nothing to say the files are no longer being listened to — and until
 *  now there was no way to undo it short of typing 0:00:00 and knowing that a
 *  zero happens to fall through. So the source is stated wherever the number is
 *  editable, and the measured value being overridden is named rather than
 *  alluded to: you can't choose between two numbers you can't both see. */
/** The manual mark's tooltip — shared, so the badge beside the name and the one
 *  on the timecode can never drift into saying different things. */
function mTitle(w: Workflow): string {
  const m = measuredRt(w);
  return `Runtime entered by hand${m != null ? ` — the audio files measure ${fmtRt(m)}` : ""}`;
}

function RuntimeSource({ w, onUseMeasured }: { w: Workflow; onUseMeasured: () => void }) {
  if (w.batches?.length) {
    return <span className="wf-rtsrc">Summed from {w.batches.length} batches.</span>;
  }
  const measured = measuredRt(w);
  if (!w.runtime_seconds) {
    return measured
      ? <span className="wf-rtsrc linked"><i>◈</i>Measured from the audio files.</span>
      : <span className="wf-rtsrc">No audio measured yet — type a runtime to set one.</span>;
  }
  return (
    <span className="wf-rtsrc manual">
      {/* The same mark the HH:MM:SS column carries, so the badge on the row and
          the explanation here are recognisably the same statement. */}
      <i className="wf-mtag">M</i>Entered by hand.
      {measured != null && (
        <>
          {" "}The audio files measure <b>{fmtRt(measured)}</b>.{" "}
          <button className="wf-rtuse" onClick={onUseMeasured}
            title="Drop the manual figure and follow the audio files again">Use measured</button>
        </>
      )}
    </span>
  );
}

const isNA = (v: string | null) => v === "NA";

// Build a single-field patch without tripping TS's computed-key/cast rules.
function set<K extends keyof WorkflowPatch>(field: K, value: WorkflowPatch[K]): WorkflowPatch {
  const p: WorkflowPatch = {};
  (p as Record<string, unknown>)[field as string] = value;
  return p;
}

// Links — per-type icon + a paste-to-add editor with domain auto-filing.
type LinkCol = "link_script" | "link_review" | "link_review_pw" | "link_folder";
type LinkField = "script" | "review" | "pass" | "folder";
const LINK_META: Record<LinkField, { label: string; col: LinkCol; icon: string; act: string; host?: string }> = {
  script: { label: "Script", col: "link_script", icon: "download", act: "Download the script", host: "drive.google" },
  review: { label: "Review", col: "link_review", icon: "external", act: "Open the review", host: "frame.io" },
  pass:   { label: "Pass",   col: "link_review_pw", icon: "copy", act: "Copy the password" },
  folder: { label: "Folder", col: "link_folder", icon: "external", act: "Open the folder", host: "box.com" },
};
const LINK_ORDER: LinkField[] = ["script", "review", "pass", "folder"];
function hostRoute(val: string): LinkField | null {
  const v = val.toLowerCase();
  for (const f of LINK_ORDER) { const h = LINK_META[f].host; if (h && v.indexOf(h) >= 0) return f; }
  return null;
}
function openUrl(val: string) { const u = /^https?:\/\//.test(val) ? val : "https://" + val.replace(/^\/+/, ""); window.open(u, "_blank", "noopener"); }

// ---------------------------------------------------------------------------
// Column model
// ---------------------------------------------------------------------------
type ColId =
  | "title" | "notes" | "service" | "category" | "status" | "proofer" | "reviewer"
  | "client" | "hh" | "mm" | "ss" | "hms" | "frac" | "start" | "timeline"
  | "delivery" | "links";
const COLDEF: Record<ColId, { label: string; min: number; w: number }> = {
  title: { label: "Project", min: 150, w: 240 },
  notes: { label: "Notes", min: 120, w: 220 },
  service: { label: "Service", min: 64, w: 84 },
  category: { label: "Service category", min: 90, w: 128 }, status: { label: "Status", min: 96, w: 116 },
  proofer: { label: "Proofer", min: 110, w: 132 }, reviewer: { label: "Tech reviewer", min: 110, w: 132 },
  client: { label: "Client", min: 72, w: 96 }, hh: { label: "HH", min: 36, w: 46 }, mm: { label: "MM", min: 36, w: 46 },
  ss: { label: "SS", min: 36, w: 46 }, hms: { label: "HH:MM:SS", min: 78, w: 92 }, frac: { label: "Frac (h)", min: 52, w: 66 },
  start: { label: "Start date", min: 104, w: 112 }, timeline: { label: "Timeline", min: 70, w: 90 },
  delivery: { label: "Delivery", min: 132, w: 138 },
  links: { label: "Assets & links", min: 256, w: 296 },
};
const COL_ORDER: ColId[] = ["title", "notes", "service", "category", "status", "proofer", "reviewer", "client", "hh", "mm", "ss", "hms", "frac", "start", "timeline", "delivery", "links"];
const SORTABLE: Partial<Record<ColId, true>> = { start: true, timeline: true, delivery: true };

interface Col { id: ColId; w: number }
const COLS_KEY = "wf_board_cols_v1";
const FMT_KEY = "wf_board_fmt";
const WIDE_KEY = "wf_board_wide";
const FILTER_KEY = "wf_board_filter_v1";
const GROUP_KEY = "wf_board_groupby";
const NOTES_KEY = "wf_board_notes";
function loadCols(): Col[] {
  try {
    const raw = localStorage.getItem(COLS_KEY);
    if (raw) {
      const a = JSON.parse(raw) as Col[];
      if (Array.isArray(a)) {
        const seen = new Set<string>(); const out: Col[] = [];
        a.forEach((c) => { if (c && COLDEF[c.id] && !seen.has(c.id)) { seen.add(c.id); out.push({ id: c.id, w: Math.max(COLDEF[c.id].min, +c.w || COLDEF[c.id].w) }); } });
        // A column the stored layout has never heard of goes where it BELONGS,
        // not on the end. Appending is why Notes arrived past "Assets & links"
        // for every board that predated it — the far end of a horizontal
        // scroll from the gap beside Project it was meant to fill. Each missing
        // column is anchored after its nearest preceding COL_ORDER neighbour
        // that the layout does have, so the user's own ordering survives and
        // the intended position is still honoured.
        COL_ORDER.forEach((id, i) => {
          if (seen.has(id)) return;
          let at = 0;
          for (let j = i - 1; j >= 0; j--) {
            const k = out.findIndex((c) => c.id === COL_ORDER[j]);
            if (k >= 0) { at = k + 1; break; }
          }
          out.splice(at, 0, { id, w: COLDEF[id].w });
          seen.add(id);
        });
        if (out.length) return out;
      }
    }
  } catch { /* ignore */ }
  return COL_ORDER.map((id) => ({ id, w: COLDEF[id].w }));
}

/** The filter outlives the visit: "I don't want to look at finished jobs" is a
 *  standing preference, not a question you re-ask every morning. Everything
 *  unrecognised is dropped, so a status retired later can't hide rows forever. */
function loadFilter(): { statuses: Set<StatusKey>; conds: Set<CondKey>; folders: Set<string> } {
  try {
    const raw = localStorage.getItem(FILTER_KEY);
    if (raw) {
      const o = JSON.parse(raw) as { statuses?: string[]; conds?: string[]; folders?: string[] };
      const st = (o.statuses ?? []).filter((k): k is StatusKey => !!STATUS[k as StatusKey]);
      const cd = (o.conds ?? []).filter((k): k is CondKey => k === "due" || k === "staffing");
      // Folder ids can't be checked here — the folders haven't loaded yet — so
      // they're pruned once they have, in the page. See `pruneFolderSel`.
      const fd = (o.folders ?? []).filter((k) => typeof k === "string");
      // A stored set naming every status is just "all"; keep the empty form.
      return { statuses: new Set(st.length === STATUS_ORDER.length ? [] : st), conds: new Set(cd), folders: new Set(fd) };
    }
  } catch { /* ignore */ }
  return { statuses: new Set(), conds: new Set(), folders: new Set() };
}
function saveFilter(statuses: Set<StatusKey>, conds: Set<CondKey>, folders: Set<string>) {
  try {
    localStorage.setItem(FILTER_KEY, JSON.stringify({
      statuses: [...statuses], conds: [...conds], folders: [...folders],
    }));
  } catch { /* ignore */ }
}

/** Which folder a row counts as being in — a row pointing at a folder that has
 *  since been deleted reads as Unfiled, exactly as the Folder grouping shows it,
 *  rather than belonging to something that isn't there. */
function folderOf(w: Workflow, known: Set<string>): string {
  const id = w.folder_id || "";
  return id && known.has(id) ? id : UNFILED;
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------
type GroupBy = "status" | "proofer" | "client" | "category" | "folder";
/** The Unfiled heading's key. "" is also what the API reads as "unfile me",
 *  so dropping a row there needs no special case on the way out. */
const UNFILED = "";
interface Group { key: string; name: string; bar: string; hint?: string; items: Workflow[] }

// ---------------------------------------------------------------------------
// Popover state
// ---------------------------------------------------------------------------
type TextField = "service" | "category" | "client";
type Suggestions = Record<TextField, string[]>;
type Pop =
  | { kind: "status"; id: string; rect: DOMRect }
  | { kind: "people"; id: string; role: "proofer" | "reviewer"; rect: DOMRect }
  | { kind: "assign"; id: string; projectId: string; rect: DOMRect }
  | { kind: "cal"; id: string; field: "start_date" | "delivery_date"; rect: DOMRect }
  | { kind: "link"; id: string; field: LinkField; rect: DOMRect }
  | { kind: "text"; id: string; field: TextField; label: string; placeholder: string; rect: DOMRect }
  | { kind: "runtime"; id: string; rect: DOMRect }
  | { kind: "notes"; id: string; rect: DOMRect }
  | { kind: "label"; id: string; rect: DOMRect }
  | null;

export function Workflows() {
  // Everything below reads today()'s date - "2d left", "due this week", the
  // calendar's own highlight. This makes the page re-render when the day turns,
  // so a tab left open overnight is not a day out the next morning.
  useDayTick();
  const { data: me, isLoading: meLoading } = useMe();
  const { list, createWorkflow, updateWorkflow, deleteWorkflow } = useWorkflowBoard();
  const { folders, foldersLoaded, createFolder, renameFolder, deleteFolder } = useWorkflowFolders();
  const { views, saveView, updateView, deleteView } = useBoardViews();
  const [renamingFolder, setRenamingFolder] = useState<string | null>(null);
  // The Timeline's own two knobs live up here so a saved view can capture them.
  const [tlGroupBy, setTlGroupBy] = useState<TlGroupBy>("proofer");
  const [tlMode, setTlMode] = useState<"load" | "hours">("hours");
  const [activeView, setActiveView] = useState<string | null>(null);
  const [viewMenu, setViewMenu] = useState<DOMRect | null>(null);
  const [savingView, setSavingView] = useState(false);
  const appliedDefault = useRef(false);
  const [folderToDelete, setFolderToDelete] = useState<WorkflowFolder | null>(null);
  // Folder upkeep reachable from the filter row. The group heading's own
  // Rename/Delete only exist while you're grouped BY folder — and now that you
  // can filter by one instead, that's exactly when you aren't.
  const [folderMenu, setFolderMenu] = useState<{ id: string; rect: DOMRect } | null>(null);
  const [folderRename, setFolderRename] = useState<WorkflowFolder | null>(null);

  // Sticky, like the other board settings: filing jobs into folders is only
  // worth doing if the board still shows them tomorrow. Without this, a reload
  // drops you back to Status and the folders look like they've gone.
  const [groupBy, setGroupBy] = useState<GroupBy>(() => {
    const v = localStorage.getItem(GROUP_KEY);
    return v === "proofer" || v === "client" || v === "category" || v === "folder" ? v : "status";
  });
  const [sort, setSort] = useState<{ field: ColId | null; dir: 1 | -1 }>({ field: null, dir: 1 });
  // Empty = every status. Kept as a set rather than one key so the board can
  // say "these five" - which is what hiding a status actually means.
  const [statuses, setStatuses] = useState<Set<StatusKey>>(() => loadFilter().statuses);
  const [conds, setConds] = useState<Set<CondKey>>(() => loadFilter().conds);
  // Folder ids, same shape as the statuses: empty means every folder.
  const [folderSel, setFolderSel] = useState<Set<string>>(() => loadFilter().folders);
  const [q, setQ] = useState("");
  const [format, setFormat] = useState<"rich" | "minimal">(() => (localStorage.getItem(FMT_KEY) === "minimal" ? "minimal" : "rich"));
  const [wide, setWide] = useState(() => localStorage.getItem(WIDE_KEY) === "1");
  // Off by default: a notepad you haven't written in yet is a wide empty column
  // between the two things you actually read. On once you've used it.
  const [notesOn, setNotesOn] = useState(() => localStorage.getItem(NOTES_KEY) === "1");
  const [cols, setCols] = useState<Col[]>(loadCols);
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());
  const [view, setView] = useState<"board" | "timeline">("board");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [pop, setPop] = useState<Pop>(null);
  const [dropGroup, setDropGroup] = useState<string | null>(null);
  // By id, not index: the rendered set can be a column short of `cols`, and a
  // position in one is not a position in the other.
  const [colOver, setColOver] = useState<ColId | null>(null);

  const dragId = useRef<string | null>(null);
  const colFrom = useRef<ColId | null>(null);

  useEffect(() => { localStorage.setItem(FMT_KEY, format); }, [format]);
  useEffect(() => { localStorage.setItem(WIDE_KEY, wide ? "1" : "0"); }, [wide]);
  useEffect(() => { localStorage.setItem(NOTES_KEY, notesOn ? "1" : "0"); }, [notesOn]);
  useEffect(() => { saveFilter(statuses, conds, folderSel); }, [statuses, conds, folderSel]);
  useEffect(() => { try { localStorage.setItem(GROUP_KEY, groupBy); } catch { /* ignore */ } }, [groupBy]);
  useEffect(() => { try { localStorage.setItem(COLS_KEY, JSON.stringify(cols)); } catch { /* ignore */ } }, [cols]);

  // Close popovers on Escape / outside click / scroll.
  useEffect(() => {
    if (!pop) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPop(null); };
    const onDown = (e: MouseEvent) => { if (!(e.target as HTMLElement).closest(".wf-pop")) setPop(null); };
    // Only a scroll that moves the anchor should close it. A text field scrolls
    // itself when a long link is pasted or a selection is dragged past its edge,
    // and the popover's own lists scroll too — none of those move the anchor.
    const onScroll = (e: Event) => {
      const t = e.target as HTMLElement;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t.closest?.(".wf-pop")) return;
      setPop(null);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("scroll", onScroll, true);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("mousedown", onDown, true); window.removeEventListener("scroll", onScroll, true); };
  }, [pop]);

  const byId = useMemo(() => new Map(list.map((w) => [w.id, w])), [list]);
  const people = useMemo(() => {
    const s = new Set<string>();
    list.forEach((w) => { [w.proofer, w.reviewer].forEach((p) => { if (p && !isNA(p)) s.add(p); }); });
    return Array.from(s).sort();
  }, [list]);
  // Distinct values already used — offered as dropdown suggestions so the option
  // list for Service / Service category / Client grows as records are added.
  const suggestions = useMemo(() => {
    const svc = new Set<string>(), cat = new Set<string>(), cli = new Set<string>();
    list.forEach((w) => { if (w.service) svc.add(w.service); if (w.category) cat.add(w.category); if (w.client) cli.add(w.client); });
    return { service: [...svc].sort(), category: [...cat].sort(), client: [...cli].sort() };
  }, [list]);

  // Projects available to "pull in" (shares the sidebar's cache).
  const navigate = useNavigate();
  const { data: projectsData } = useQuery({
    queryKey: ["projects", "active"],
    queryFn: () => api<{ projects: Project[] }>("/api/projects").then((r) => r.projects),
  });
  const projects = projectsData ?? [];
  const [addMenu, setAddMenu] = useState<DOMRect | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const linkedIds = useMemo(() => new Set(list.map((w) => w.project_id).filter(Boolean) as string[]), [list]);
  const byProjId = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  // Per-row kebab (rename / archive / delete), mirroring the Projects view.
  const qc = useQueryClient();
  const [kebab, setKebab] = useState<{ id: string; rect: DOMRect } | null>(null);
  const [renameTarget, setRenameTarget] = useState<Workflow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Workflow | null>(null);
  const [dupTarget, setDupTarget] = useState<Workflow | null>(null);
  const [deleting, setDeleting] = useState(false);

  // A folder the selection names can be deleted out from under it — by you in
  // another tab, or by a teammate. Left alone the board would then filter to a
  // folder that no longer exists and show nothing, with a chip row that can't
  // explain why. Pruning on load turns that into "no folder filter".
  const knownFolderIds = useMemo(() => new Set(folders.map((f) => f.id)), [folders]);
  useEffect(() => {
    // Gated on LOADED, not on "there are some". Deleting the last folder is the
    // strongest reason to prune, and `folders.length` was treating it as a
    // reason to skip: the selection survived, the chip row vanished with the
    // last folder, and the board sat at "Showing 0 of 7" with nothing left on
    // screen to explain it or take it back.
    if (!foldersLoaded || !folderSel.size) return;
    const kept = [...folderSel].filter((id) => id === UNFILED || knownFolderIds.has(id));
    if (kept.length !== folderSel.size) setFolderSel(new Set(kept));
  }, [knownFolderIds, foldersLoaded]);  // eslint-disable-line react-hooks/exhaustive-deps

  // What a view IS. Everything that changes what you're looking at, and nothing
  // that changes moment to moment: the search box is deliberately left out — a
  // view is an arrangement, not a query you were halfway through typing.
  const currentState = (): Record<string, unknown> => ({
    v: 1,
    view, groupBy, sort, format, wide, notes: notesOn,
    statuses: [...statuses], conds: [...conds], folders: [...folderSel],
    tl: { groupBy: tlGroupBy, mode: tlMode },
    cols: cols.map((c) => ({ id: c.id, w: c.w })),
  });

  /** Every field is optional and separately guarded: a view saved before a
   *  setting existed has to open, and so does one saved after a setting was
   *  retired. A view that can't be read in full still applies the parts it can. */
  const applyState = (raw: Record<string, unknown>) => {
    const st = raw as Partial<{
      view: string; groupBy: string; sort: { field: ColId | null; dir: 1 | -1 };
      format: string; wide: boolean; notes: boolean; statuses: string[]; conds: string[]; folders: string[];
      tl: { groupBy: string; mode: string }; cols: Col[];
    }>;
    if (st.view === "board" || st.view === "timeline") setView(st.view);
    if (st.groupBy && ["status", "proofer", "client", "category", "folder"].includes(st.groupBy)) setGroupBy(st.groupBy as GroupBy);
    if (st.sort && (st.sort.field === null || COLDEF[st.sort.field as ColId])) setSort({ field: st.sort.field, dir: st.sort.dir === -1 ? -1 : 1 });
    if (st.format === "rich" || st.format === "minimal") setFormat(st.format);
    if (typeof st.wide === "boolean") setWide(st.wide);
    if (typeof st.notes === "boolean") setNotesOn(st.notes);
    if (Array.isArray(st.statuses)) setStatuses(new Set(st.statuses.filter((k): k is StatusKey => !!STATUS[k as StatusKey])));
    if (Array.isArray(st.conds)) setConds(new Set(st.conds.filter((k): k is CondKey => k === "due" || k === "staffing")));
    // Not validated against the live folders here: they may not have loaded yet,
    // and the prune effect above is what settles it either way.
    if (Array.isArray(st.folders)) setFolderSel(new Set(st.folders.filter((k) => typeof k === "string")));
    if (st.tl?.groupBy && ["status", "proofer", "client"].includes(st.tl.groupBy)) setTlGroupBy(st.tl.groupBy as TlGroupBy);
    if (st.tl?.mode === "load" || st.tl?.mode === "hours") setTlMode(st.tl.mode);
    if (Array.isArray(st.cols)) {
      const seen = new Set<string>(); const out: Col[] = [];
      st.cols.forEach((c) => { if (c && COLDEF[c.id] && !seen.has(c.id)) { seen.add(c.id); out.push({ id: c.id, w: Math.max(COLDEF[c.id].min, +c.w || COLDEF[c.id].w) }); } });
      COL_ORDER.forEach((id) => { if (!seen.has(id)) out.push({ id, w: COLDEF[id].w }); });
      if (out.length) setCols(out);
    }
  };

  const openView = (v: BoardView) => { applyState(v.state); setActiveView(v.id); };

  // The default view opens the board — once. After that the settings are yours
  // to change, and re-applying on every render would fight every click.
  useEffect(() => {
    if (appliedDefault.current || !views.length) return;
    appliedDefault.current = true;
    const d = views.find((v) => v.is_default);
    if (d) openView(d);
  }, [views]);

  if (meLoading) return <div className="page muted">Loading…</div>;
  if (me && !me.entitlements?.workflows) return <Navigate to="/" replace />;

  // --- derived list (filter + search + sort) ---
  const filtered = list.filter((w) => {
    if (statuses.size && !statuses.has(w.status as StatusKey)) return false;
    if (folderSel.size && !folderSel.has(folderOf(w, knownFolderIds))) return false;
    if (conds.has("due") && !isDueSoon(w)) return false;
    if (conds.has("staffing") && !hasOpenRole(w)) return false;
    if (q && `${w.name} ${w.subtitle} ${w.client} ${w.service}`.toLowerCase().indexOf(q.toLowerCase()) < 0) return false;
    return true;
  });
  const sorted = sortList(filtered, sort);
  const groups = groupsFor(sorted, groupBy, folders);

  // --- grid template ---
  // Hiding filters the RENDERED set rather than editing `cols`: loadCols re-adds
  // any column missing from storage — that's how a newly shipped column reaches
  // an existing board — so absence can't mean hidden. This way the column also
  // keeps its width and its place in the order for when it comes back.
  const shownCols = notesOn ? cols : cols.filter((c) => c.id !== "notes");
  const gridTemplate = shownCols.map((c) => `${c.w}px`).join(" ") + " minmax(24px,1fr)";
  const minW = shownCols.reduce((a, c) => a + c.w, 0) + 24;

  // --- actions ---
  const patch = (id: string, p: WorkflowPatch) => updateWorkflow(id, p);
  const toggleSort = (id: ColId) => {
    setSort((s) => s.field !== id ? { field: id, dir: 1 } : s.dir === 1 ? { field: id, dir: -1 } : { field: null, dir: 1 });
  };
  const setGroupValue = (w: Workflow, groupKey: string) => {
    if (groupBy === "proofer") patch(w.id, { proofer: groupKey === "__na" ? "NA" : groupKey === "__none" ? null : groupKey });
    else if (groupBy === "folder") patch(w.id, { folder_id: groupKey });  // "" ⇒ unfile
    else patch(w.id, set(groupBy, groupKey));
  };
  // Was "New workflow", which made a blank row that called itself "Untitled
  // project" — so the board offered two ways to make what looked like the same
  // thing, and the one that wasn't a project said it was. A folder is the thing
  // that board actually lacked: somewhere to put the projects it already has.
  const newFolder = async () => {
    const f = await createFolder("New folder");
    if (f) { setGroupBy("folder"); setRenamingFolder(f.id); }
  };
  // Pull existing projects onto the board (one linked, tracked row each).
  const addExisting = (sel: Project[]) => { sel.forEach((p) => createWorkflow({ project_id: p.id, name: p.name, status: projectToStatus(p) })); setPickerOpen(false); };
  const openProject = (pid: string) => navigate(`/projects/${pid}`);

  // Row kebab actions. A linked row also acts on the underlying project (like the
  // Projects view); a standalone tracker row acts only on itself.
  const invalidateProjects = () => qc.invalidateQueries({ queryKey: ["projects"] });
  const doRename = async (w: Workflow, name: string) => {
    const nm = name.trim();
    if (!nm) { setRenameTarget(null); return; }
    updateWorkflow(w.id, { name: nm });                       // rename the board row
    if (w.project_id) {                                        // …and the linked project, in sync
      try { await api(`/api/projects/${w.project_id}/rename`, { method: "POST", body: JSON.stringify({ name: nm }) }); invalidateProjects(); }
      catch (e: any) { toast(e?.message || "Couldn't rename the project.", "error"); }
    }
    setRenameTarget(null);
    toast("Renamed.", "success");
  };
  const doArchive = async (w: Workflow) => {
    if (!w.project_id) return;                                 // archive is a project action
    try { await api(`/api/projects/${w.project_id}/archive`, { method: "POST", body: JSON.stringify({ archived: true }) }); invalidateProjects(); toast("Project archived — still tracked here.", "info"); }
    catch (e: any) { toast(e?.message || "Couldn't archive — try again?", "error"); }
  };
  const doDelete = async (w: Workflow) => {
    setDeleting(true);
    if (w.project_id) {                                        // delete the project + files, then the row
      try { await api(`/api/projects/${w.project_id}`, { method: "DELETE" }); invalidateProjects(); }
      catch (e: any) { toast(e?.message || "Couldn't delete the project.", "error"); setDeleting(false); return; }
    }
    deleteWorkflow(w.id);
    if (drawerId === w.id) setDrawerId(null);
    setDeleting(false);
    setDeleteTarget(null);
    toast(w.project_id ? "Project deleted." : "Removed from the board.", "info");
  };

  // --- column resize (mouse) ---
  const startResize = (id: ColId, e: ReactMouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    const cur = cols.find((c) => c.id === id);
    if (!cur) return;
    const startX = e.clientX; const startW = cur.w; const min = COLDEF[id].min;
    document.body.classList.add("wf-resizing");
    const mv = (ev: MouseEvent) => setCols((c) => c.map((col) => col.id === id ? { ...col, w: Math.max(min, Math.round(startW + (ev.clientX - startX))) } : col));
    const up = () => { document.removeEventListener("mousemove", mv); document.removeEventListener("mouseup", up); document.body.classList.remove("wf-resizing"); };
    document.addEventListener("mousemove", mv); document.addEventListener("mouseup", up);
  };

  const wf = drawerId ? byId.get(drawerId) : undefined;

  return (
    <div className={`page wf-board${format === "minimal" ? " fmt-minimal" : ""}${wide ? " wf-wide" : ""}`}>
      <div className="wf-head">
        <div>
          <div className="wf-eyebrow">Mockingbird · Production</div>
          <h1 className="wf-h1">Workflows</h1>
          <p className="wf-sub">Every job a client is waiting on — the service, who's on it, when it's due, and where it stands. Drag a row between statuses; open a row for the full detail.</p>
        </div>
        <div className="wf-head-actions">
          <button className="btn" onClick={newFolder} title="Group jobs on the board — a box set, a client slate, a month">＋ New folder</button>
          <button className="btn primary" onClick={(e) => setAddMenu(e.currentTarget.getBoundingClientRect())}>＋ Add projects</button>
        </div>
      </div>

      <FilterBar list={list} statuses={statuses} setStatuses={setStatuses}
        conds={conds} setConds={setConds} shown={filtered.length}
        folders={folders} folderSel={folderSel} setFolderSel={setFolderSel}
        knownFolderIds={knownFolderIds}
        onFolderMenu={(id, rect) => setFolderMenu((m) => (m && m.id === id ? null : { id, rect }))}
        display={{
          minimal: format === "minimal", setMinimal: (v) => setFormat(v ? "minimal" : "rich"),
          notes: notesOn, setNotes: setNotesOn,
          wide, setWide,
        }} />

      {/* Saved views. A row of their own, above the controls they capture, so
          the order reads the way it works: pick an arrangement, then adjust it. */}
      <div className="wf-views">
        <span className="wf-flbl">Views</span>
        {views.map((v) => (
          <button key={v.id} className={`wf-vchip${activeView === v.id ? " on" : ""}`}
            aria-pressed={activeView === v.id}
            title={v.is_default ? `${v.name} — opens the board` : v.name}
            onClick={() => openView(v)}
            onDoubleClick={() => setViewMenu(null)}>
            {v.is_default && <span className="wf-vstar" aria-label="Opens the board">★</span>}
            {v.name || "Untitled view"}
            <span className="wf-vx" role="button" tabIndex={0}
              aria-label={`Options for ${v.name}`}
              onClick={(e) => { e.stopPropagation(); setViewMenu(viewMenu ? null : (e.currentTarget as HTMLElement).getBoundingClientRect()); setActiveView(v.id); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setViewMenu((e.currentTarget as HTMLElement).getBoundingClientRect()); setActiveView(v.id); } }}>⋯</span>
          </button>
        ))}
        {!views.length && <span className="wf-vhint">Set the board up the way you like it, then save it here.</span>}
        <button className="wf-vsave" onClick={() => setSavingView(true)}>＋ Save this view</button>
      </div>

      <div className="wf-toolbar">
        <div className="wf-seg" role="tablist" aria-label="View">
          <button className={view === "board" ? "on" : ""} aria-selected={view === "board"}
            onClick={() => setView("board")}>Board</button>
          <button className={view === "timeline" ? "on" : ""} aria-selected={view === "timeline"}
            onClick={() => setView("timeline")}
            title="The same rows on a date axis — what's due when, and who's booked">Timeline</button>
          <button disabled title="Coming in a later round">Calendar</button>
        </div>
        <div className="wf-tool-right">
          <div className="wf-field">
            <label htmlFor="wf-groupby">Group by</label>
            <select id="wf-groupby" value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}>
              <option value="status">Status</option>
              <option value="proofer">Proofer</option>
              <option value="client">Client</option>
              <option value="category">Service category</option>
              <option value="folder">Folder</option>
            </select>
          </div>
          <div className="wf-search">
            <Icon name="search" size={13} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects…" aria-label="Search projects" />
          </div>
        </div>
      </div>

      {view === "timeline" ? (
        <TimelineView
          rows={sorted}
          groupBy={tlGroupBy}
          onGroupBy={setTlGroupBy}
          mode={tlMode}
          onMode={setTlMode}
          statusMeta={sMeta}
          statusOrder={STATUS_ORDER}
          onOpenProject={openProject}
          onPatch={(id, p) => patch(id, p)}
          onOpen={(id) => {
            // Opening from the timeline drops you back on the board with that
            // row expanded — one place to actually edit, no duplicate editor.
            setView("board");
            setOpenRows((s) => new Set(s).add(id));
          }}
        />
      ) : (
      <div className="wf-scroll">
        <div className="wf-grid-board" style={{ "--wf-grid": gridTemplate, minWidth: minW } as CSSProperties}>
          {/* column header */}
          <div className="wf-colhead" style={{ gridTemplateColumns: "var(--wf-grid)" }}>
            {shownCols.map((c) => {
              const sortable = !!SORTABLE[c.id]; const active = sort.field === c.id;
              return (
                <span key={c.id} className={`wf-colh${sortable ? " sortable" : ""}${colOver === c.id ? " over" : ""}`}
                  draggable
                  title={sortable ? "Click to sort · drag to reorder" : "Drag to reorder"}
                  onClick={(e) => { if (sortable && !(e.target as HTMLElement).closest(".wf-rz")) toggleSort(c.id); }}
                  onDragStart={(e) => { colFrom.current = c.id; e.dataTransfer.effectAllowed = "move"; }}
                  onDragOver={(e) => { if (colFrom.current != null) { e.preventDefault(); setColOver(c.id); } }}
                  onDrop={(e) => {
                    if (colFrom.current != null) {
                      e.preventDefault(); const from = colFrom.current;
                      if (from !== c.id) setCols((cc) => {
                        const next = cc.slice();
                        const fi = next.findIndex((x) => x.id === from);
                        const ti = next.findIndex((x) => x.id === c.id);
                        if (fi < 0 || ti < 0) return cc;
                        const [m] = next.splice(fi, 1); next.splice(ti, 0, m); return next;
                      });
                    }
                    colFrom.current = null; setColOver(null);
                  }}
                  onDragEnd={() => { colFrom.current = null; setColOver(null); }}
                >
                  <span className="wf-collbl">{COLDEF[c.id].label}</span>
                  {sortable && <span className={`wf-sortind${active ? " on" : ""}`}>{active ? (sort.dir === 1 ? "↑" : "↓") : "↕"}</span>}
                  <span className="wf-rz" onMouseDown={(e) => startResize(c.id, e)} title="Drag to resize" />
                </span>
              );
            })}
          </div>

          {/* groups + rows */}
          {groups.length === 0 && <div className="wf-empty">Nothing here yet. Use <b>Add projects</b> to pull in the projects you want to track.</div>}
          {groups.map((gr) => {
            const isCol = collapsed.has(gr.name);
            return (
              <div key={gr.key} className={`wf-group${isCol ? " collapsed" : ""}${dropGroup === gr.key ? " drop-over" : ""}`}
                onDragOver={(e) => { if (dragId.current) { e.preventDefault(); setDropGroup(gr.key); } }}
                onDragLeave={(e) => { if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setDropGroup((d) => (d === gr.key ? null : d)); }}
                onDrop={(e) => {
                  if (dragId.current) { e.preventDefault(); const w = byId.get(dragId.current); if (w) setGroupValue(w, gr.key); }
                  dragId.current = null; setDropGroup(null);
                }}
              >
                <div className="wf-grouphead" title={gr.hint} onClick={() => setCollapsed((s) => { const n = new Set(s); n.has(gr.name) ? n.delete(gr.name) : n.add(gr.name); return n; })}>
                  <span className="wf-groupbar" style={{ background: gr.bar }} />
                  {renamingFolder === gr.key && gr.key !== UNFILED ? (
                    <input className="wf-fname" autoFocus defaultValue={gr.name}
                      /* Selected on focus: a new folder arrives pre-named "New
                         folder" with the cursor already in it, so typing has to
                         replace that placeholder rather than append to it. */
                      onFocus={(e) => e.target.select()}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== gr.name) renameFolder(gr.key, v); setRenamingFolder(null); }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                        if (e.key === "Escape") { e.stopPropagation(); setRenamingFolder(null); }
                      }} />
                  ) : (
                    <span className="wf-groupname">{gr.name}</span>
                  )}
                  <span className="wf-groupcount">{gr.items.length}</span>
                  {groupBy === "folder" && gr.key !== UNFILED && renamingFolder !== gr.key && (
                    <span className="wf-fbtns" onClick={(e) => e.stopPropagation()}>
                      <button className="wf-fbtn" onClick={() => setRenamingFolder(gr.key)} title="Rename this folder">Rename</button>
                      <button className="wf-fbtn" onClick={() => setFolderToDelete(folders.find((f) => f.id === gr.key) ?? null)} title="Delete this folder">Delete</button>
                    </span>
                  )}
                  <span className="wf-chev">{isCol ? "▸" : "▾"}</span>
                </div>
                {!isCol && (
                  <div className="wf-rows">
                    {gr.items.map((w) => (
                      <Row key={w.id} w={w} cols={shownCols}
                        open={openRows.has(w.id)}
                        onToggleOpen={() => setOpenRows((s) => { const n = new Set(s); n.has(w.id) ? n.delete(w.id) : n.add(w.id); return n; })}
                        onOpenDrawer={() => setDrawerId(w.id)}
                        onDragStart={() => { dragId.current = w.id; }}
                        onDragEnd={() => { dragId.current = null; setDropGroup(null); }}
                        onPop={setPop}
                        onLinkDo={(field) => linkDo(w, field)}
                        onOpenProject={openProject}
                        onKebab={(rect) => setKebab({ id: w.id, rect })}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {view === "board" && <div className="wf-hint">Drag a row onto another {groupBy === "status" ? "status" : groupBy} to move it. Drag a column header to reorder, its edge to resize. Click a row for detail.</div>}

      {/* popovers */}
      {pop && <Popover pop={pop} wf={byId.get(pop.id)} people={people} suggestions={suggestions} onClose={() => setPop(null)} onPatch={patch} />}

      {/* Add-projects menu */}
      {addMenu && (
        <>
          <div className="wf-catch" onClick={() => setAddMenu(null)} />
          <div className="wf-pop wf-menu" style={{ position: "fixed", left: Math.max(8, Math.min(addMenu.left, window.innerWidth - 250)), top: addMenu.bottom + 6 }}>
            <div className="wf-menu-h">Add projects</div>
            <button onClick={() => { setAddMenu(null); setPickerOpen(true); }}><Icon name="folder" size={15} />Pull in existing project…</button>
            <button onClick={() => { setAddMenu(null); navigate("/new", { state: { trackInWorkflows: true } }); }}><Icon name="plus" size={15} />Create a new project…</button>
          </div>
        </>
      )}
      {pickerOpen && (
        <ProjectPicker
          projects={projects.filter((p) => !p.archived && !linkedIds.has(p.id))}
          onClose={() => setPickerOpen(false)}
          onAdd={addExisting}
        />
      )}

      {/* Row kebab (rename / archive / delete) */}
      {kebab && (() => {
        const w = byId.get(kebab.id); if (!w) return null;
        return (
          <>
            <div className="wf-catch" onClick={() => setKebab(null)} />
            <div className="wf-pop wf-menu" style={anchored(kebab.rect, { width: 212 })}>
              <button onClick={() => { setKebab(null); setRenameTarget(w); }}><Icon name="edit" size={15} />Rename…</button>
              {w.project_id && <button onClick={() => { setKebab(null); setDupTarget(w); }}><Icon name="copy" size={15} />Duplicate project…</button>}
              {w.project_id && <button onClick={() => { setKebab(null); doArchive(w); }}><Icon name="archive" size={15} />Archive project</button>}
              <button className="wf-danger-item" onClick={() => { setKebab(null); setDeleteTarget(w); }}><Icon name="trash" size={15} />{w.project_id ? "Delete project…" : "Remove from board"}</button>
            </div>
          </>
        );
      })()}
      {dupTarget?.project_id && (
        <DuplicateProjectDialog
          projectId={dupTarget.project_id}
          projectName={byProjId.get(dupTarget.project_id)?.name || dupTarget.name}
          onClose={() => setDupTarget(null)}
          // Duplicating from the board keeps you on the board, so the copy has
          // to appear ON it — otherwise the action looks like it did nothing.
          // Announced, not the source row's status: a copy is a job that has
          // not started, whatever the project it was made from has done.
          onDone={(id, nm) => createWorkflow({ project_id: id, name: nm, status: "announced" })}
        />
      )}
      {renameTarget && <RenameDialog initial={renameTarget.name} onSave={(name) => doRename(renameTarget, name)} onCancel={() => setRenameTarget(null)} />}
      {savingView && (
        <SaveViewDialog
          existing={views.find((v) => v.id === activeView)?.name}
          canReplace={!!views.find((v) => v.id === activeView)}
          onSave={async (name, mode, makeDefault) => {
            setSavingView(false);
            if (mode === "replace" && activeView) {
              await updateView(activeView, { name, state: currentState() as BoardView["state"], is_default: makeDefault });
              toast("View updated.", "success");
            } else {
              const v = await saveView(name, currentState(), makeDefault);
              if (v) setActiveView(v.id);
              toast("View saved.", "success");
            }
          }}
          onCancel={() => setSavingView(false)}
        />
      )}
      {viewMenu && activeView && (
        <>
          <div className="wf-catch" onClick={() => setViewMenu(null)} />
          <div className="wf-pop wf-menu" style={anchored(viewMenu, { width: 212, offsetLeft: -140 })}>
            <button onClick={() => { setViewMenu(null); setSavingView(true); }}>
              <Icon name="copy" size={15} />Update with current settings
            </button>
            <button onClick={async () => {
              const v = views.find((x) => x.id === activeView);
              setViewMenu(null);
              if (!v) return;
              await updateView(v.id, { is_default: !v.is_default });
              toast(v.is_default ? "No longer opens the board." : "This view now opens the board.", "info");
            }}>
              <Icon name="check" size={15} />
              {views.find((x) => x.id === activeView)?.is_default ? "Don’t open the board with this" : "Open the board with this"}
            </button>
            <button className="wf-muted" onClick={async () => {
              const v = views.find((x) => x.id === activeView);
              setViewMenu(null);
              if (!v) return;
              await deleteView(v.id);
              setActiveView(null);
              // Worth stating plainly: a view holds no work, so this is the rare
              // delete that costs nothing but the arrangement itself.
              toast("View deleted — the jobs are untouched.", "info");
            }}>
              <Icon name="trash" size={15} />Delete view
            </button>
          </div>
        </>
      )}
      {folderMenu && (() => {
        const f = folders.find((x) => x.id === folderMenu.id);
        if (!f) return null;
        const n = list.filter((w) => folderOf(w, knownFolderIds) === f.id).length;
        return (
          <>
            <div className="wf-catch" onClick={() => setFolderMenu(null)} />
            <div className="wf-pop wf-menu" style={anchored(folderMenu.rect, { width: 212, offsetLeft: -120 })}>
              <div className="wf-menu-h">{f.name || "Untitled folder"} · {n} {n === 1 ? "job" : "jobs"}</div>
              <button onClick={() => { setFolderMenu(null); setFolderRename(f); }}>
                <Icon name="edit" size={15} />Rename folder…
              </button>
              <button className="wf-muted" onClick={() => { setFolderMenu(null); setFolderToDelete(f); }}>
                <Icon name="trash" size={15} />Delete folder
              </button>
            </div>
          </>
        );
      })()}
      {folderRename && (
        <RenameDialog initial={folderRename.name}
          onSave={(name) => { renameFolder(folderRename.id, name); setFolderRename(null); toast("Folder renamed.", "success"); }}
          onCancel={() => setFolderRename(null)} />
      )}
      <ConfirmDialog
        open={!!folderToDelete}
        title="Delete this folder?"
        body={(() => {
          const n = list.filter((w) => w.folder_id === folderToDelete?.id).length;
          return (
            <>Deletes the folder <b>{folderToDelete?.name}</b>.{" "}
              {n === 0
                ? <>It’s empty, so nothing else changes.</>
                : <>The {n} {n === 1 ? "job" : "jobs"} inside {n === 1 ? "stays" : "stay"} on the board — {n === 1 ? "it" : "they"} just {n === 1 ? "moves" : "move"} to <b>Unfiled</b>. No work is deleted.</>}
            </>
          );
        })()}
        confirmLabel="Delete folder"
        danger
        onConfirm={async () => {
          const f = folderToDelete;
          setFolderToDelete(null);
          if (!f) return;
          const r = await deleteFolder(f.id);
          const n = r?.unfiled ?? 0;
          toast(n ? `Folder deleted — ${n} ${n === 1 ? "job" : "jobs"} moved to Unfiled.` : "Folder deleted.", "info");
        }}
        onCancel={() => setFolderToDelete(null)}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title={deleteTarget?.project_id ? "Delete this project?" : "Remove from the board?"}
        body={deleteTarget?.project_id
          ? <>This permanently removes <b>{deleteTarget?.name}</b> and all its files. This can’t be undone.</>
          : <>This removes <b>{deleteTarget?.name}</b> from the Workflows board. (It isn’t linked to a project.)</>}
        confirmLabel={deleteTarget?.project_id ? "Delete" : "Remove"}
        danger
        busy={deleting}
        onConfirm={() => { if (deleteTarget) doDelete(deleteTarget); }}
        onCancel={() => setDeleteTarget(null)}
      />


      {/* drawer */}
      <div className={`wf-backdrop${wf ? " on" : ""}`} onClick={() => setDrawerId(null)} />
      {wf && <Drawer wf={wf} linkedProject={wf.project_id ? byProjId.get(wf.project_id) : undefined} suggestions={suggestions} onClose={() => setDrawerId(null)} onPatch={patch} onDelete={() => { deleteWorkflow(wf.id); setDrawerId(null); }} onPop={setPop} onOpenProject={openProject} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project picker — pull existing projects onto the board as tracked rows
// ---------------------------------------------------------------------------
function ProjectPicker({ projects, onClose, onAdd }: { projects: Project[]; onClose: () => void; onAdd: (sel: Project[]) => void }) {
  const [sel, setSel] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const chosen = projects.filter((p) => sel.has(p.id));
  return (
    <div className="wf-modal-backdrop" onClick={onClose}>
      <div className="wf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wf-modal-head">
          <div>
            <div className="wf-modal-title">Pull in projects</div>
            <div className="wf-modal-sub">Select existing projects to track on the board.</div>
          </div>
          <button className="btn ghost sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="wf-modal-list">
          {projects.length === 0
            ? <div className="wf-modal-empty">All your projects are already on the board — or you don't have any yet.</div>
            : projects.map((p) => (
              <label key={p.id} className={`wf-pick-row${sel.has(p.id) ? " on" : ""}`}>
                <input type="checkbox" checked={sel.has(p.id)} onChange={() => toggle(p.id)} />
                <span className="wf-pick-name">{p.name}</span>
                <span className="wf-pick-meta">{PROJ_STATUS_LABEL[p.status] ?? p.status} · {p.n_audio} audio</span>
              </label>
            ))}
        </div>
        <div className="wf-modal-foot">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!chosen.length} onClick={() => onAdd(chosen)}>
            Add {chosen.length || ""} {chosen.length === 1 ? "project" : "projects"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Rename dialog (row + linked project), reusing the app's modal styles.
/** Saving a view has two shapes — a new one, or an update to the one you're
 *  looking at — and they're one dialog because the choice is only meaningful
 *  when a view is already open. */
function SaveViewDialog({ existing, canReplace, onSave, onCancel }: {
  existing?: string; canReplace: boolean;
  onSave: (name: string, mode: "new" | "replace", makeDefault: boolean) => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<"new" | "replace">(canReplace ? "replace" : "new");
  const [v, setV] = useState(mode === "replace" ? (existing ?? "") : "");
  const [def, setDef] = useState(false);
  const name = v.trim();
  const go = () => { if (name) onSave(name, mode, def); };
  return (
    <div className="modal-overlay" {...backdropClose(onCancel)}>
      <div className="modal-card" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h3>Save this view</h3>
        <div className="modal-body">
          <p className="muted" style={{ marginTop: 0 }}>
            Captures the board or timeline you're on, the status filters, grouping, sort and columns.
            Not the search box — that's a question, not an arrangement.
          </p>
          {canReplace && (
            <div className="wf-seg" role="group" aria-label="Save as" style={{ marginBottom: 10 }}>
              <button className={mode === "replace" ? "on" : ""}
                onClick={() => { setMode("replace"); setV(existing ?? ""); }}>Update “{existing}”</button>
              <button className={mode === "new" ? "on" : ""}
                onClick={() => { setMode("new"); setV(""); }}>Save as new</button>
            </div>
          )}
          <input className="input" autoFocus maxLength={120} value={v} placeholder="e.g. This week, unstaffed"
            onChange={(e) => setV(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); } else if (e.key === "Escape") { e.preventDefault(); onCancel(); } }} />
          <label className="wf-vdef">
            <input type="checkbox" checked={def} onChange={(e) => setDef(e.target.checked)} />
            Open the board with this view
          </label>
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn primary" disabled={!name} onClick={go}>
            {mode === "replace" ? "Update view" : "Save view"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RenameDialog({ initial, onSave, onCancel }: { initial: string; onSave: (name: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(initial);
  return (
    <div className="modal-overlay" {...backdropClose(onCancel)}>
      <div className="modal-card" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h3>Rename</h3>
        <div className="modal-body">
          <input className="input" autoFocus maxLength={120} value={v} placeholder="Name"
            onChange={(e) => setV(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (v.trim()) onSave(v.trim()); } else if (e.key === "Escape") { e.preventDefault(); onCancel(); } }} />
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn primary" disabled={!v.trim()} onClick={() => onSave(v.trim())}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// helpers used above (module scope so they can be shared)
// ---------------------------------------------------------------------------
type CondKey = "due" | "staffing";

/** Overdue counts as due: a date that has passed is the most urgent kind of
 *  soon. Complete is excluded - a delivered job is not waiting on anyone. */
function isDueSoon(w: Workflow): boolean {
  if (w.status === "complete" || !w.delivery_date) return false;
  return Math.round((+parseD(w.delivery_date) - +today()) / MS) <= 7;
}

function hasOpenRole(w: Workflow): boolean {
  // A LINKED project's proofer is its real assignments, not the typed field —
  // which stays null there. Reading the field alone counted a fully staffed
  // project as needing staffing. "NA" means the role was decided, not open.
  const prooferOpen = w.project_id
    ? !(w.assigned_proofers?.length || isNA(w.proofer))
    : w.proofer === null;
  return prooferOpen || (w.reviewer === null);
}
function sortVal(w: Workflow, field: ColId): number | null {
  if (field === "timeline") { if (!w.start_date || !w.delivery_date) return null; return Math.round((+parseD(w.delivery_date) - +parseD(w.start_date)) / MS); }
  const v = field === "start" ? w.start_date : field === "delivery" ? w.delivery_date : null;
  if (!v) return null;
  return +parseD(v);
}
function sortList(items: Workflow[], sort: { field: ColId | null; dir: 1 | -1 }): Workflow[] {
  if (!sort.field) return items;
  const f = sort.field;
  return items.slice().sort((x, y) => {
    const a = sortVal(x, f), b = sortVal(y, f);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return (a - b) * sort.dir;
  });
}
function groupsFor(items: Workflow[], groupBy: GroupBy, folders: WorkflowFolder[] = []): Group[] {
  // Folders are the one grouping whose headings do NOT come from the rows: an
  // empty folder has to be on screen, or there is nowhere to drop the first job.
  if (groupBy === "folder") {
    const byId = new Map(folders.map((f) => [f.id, { key: f.id, name: f.name || "Untitled folder", bar: "#8312E0", items: [] as Workflow[] }]));
    const unfiled: Group = { key: UNFILED, name: "Unfiled", bar: "#B7BCD0", hint: "Not in a folder yet.", items: [] };
    // A row pointing at a deleted folder lands in Unfiled rather than vanishing.
    items.forEach((w) => { (byId.get(w.folder_id || "") ?? unfiled).items.push(w); });
    return [...folders.map((f) => byId.get(f.id)!), unfiled];
  }
  if (groupBy === "status") {
    const g: Record<string, Group> = {};
    STATUS_ORDER.forEach((k) => { g[k] = { key: k, name: STATUS[k].label, bar: STATUS[k].bar, hint: STATUS[k].hint, items: [] }; });
    items.forEach((w) => { (g[w.status] || g.announced).items.push(w); });
    return STATUS_ORDER.filter((k) => g[k].items.length).map((k) => g[k]);
  }
  const g: Record<string, Group> = {}; const order: string[] = [];
  const metaOf = (w: Workflow): { key: string; name: string; bar: string } => {
    if (groupBy === "proofer") {
      const p = w.proofer;
      if (isNA(p)) return { key: "__na", name: "Not required", bar: "#B7BCD0" };
      if (!p) return { key: "__none", name: "Unassigned", bar: "#B7BCD0" };
      return { key: p, name: p, bar: avatarColor(p) };
    }
    const v = (w[groupBy] as string) || "";
    return { key: v, name: v || "—", bar: "#8312E0" };
  };
  items.forEach((w) => { const m = metaOf(w); if (!g[m.key]) { g[m.key] = { ...m, items: [] }; order.push(m.key); } g[m.key].items.push(w); });
  const rank = (k: string) => (k === "__na" ? 2 : k === "__none" ? 1 : 0);
  order.sort((a, b) => rank(a) !== rank(b) ? rank(a) - rank(b) : g[a].name.localeCompare(g[b].name));
  return order.map((k) => g[k]);
}
function linkDo(w: Workflow, field: LinkField) {
  const val = (w[LINK_META[field].col] as string) || "";
  if (!val) return;
  if (field === "pass") { navigator.clipboard?.writeText(val).catch(() => {}); return; }
  if (field === "script") {
    try { const a = document.createElement("a"); a.href = /^https?:\/\//.test(val) ? val : "https://" + val.replace(/^\/+/, ""); a.download = ""; a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove(); } catch { openUrl(val); }
    return;
  }
  openUrl(val);
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------
/** Status is the word every row already wears — the dot in its cell, the
 *  heading it sits under on the board — so a filter named after status needs
 *  no explaining. The five descriptive tiles that used to live here could say
 *  "only complete" but never "everything except complete", because they were
 *  one-at-a-time; that gap is what this replaces.
 *
 *  Two of the old tiles survive as conditions, on the second line: "due soon"
 *  and "open roles" cut ACROSS status (an in-progress job can be both), so
 *  they can't be folded into the status row without lying about the data. */
function FilterBar({ list, statuses, setStatuses, conds, setConds, shown,
  folders, folderSel, setFolderSel, knownFolderIds, onFolderMenu, display }: {
  list: Workflow[];
  statuses: Set<StatusKey>; setStatuses: (s: Set<StatusKey>) => void;
  conds: Set<CondKey>; setConds: (s: Set<CondKey>) => void;
  shown: number;
  folders: WorkflowFolder[];
  folderSel: Set<string>; setFolderSel: (s: Set<string>) => void;
  knownFolderIds: Set<string>;
  onFolderMenu: (id: string, rect: DOMRect) => void;
  /** Not filters — how the board is drawn. They live in this bar anyway because
   *  the question "what can I change here?" has one answer and it should have
   *  one place, and because three label-plus-segmented-pair clusters strung
   *  along the toolbar read as clutter rather than as options. The row label
   *  says which kind they are. */
  display: {
    minimal: boolean; setMinimal: (v: boolean) => void;
    notes: boolean; setNotes: (v: boolean) => void;
    wide: boolean; setWide: (v: boolean) => void;
  };
}) {
  const nFor = (k: StatusKey) => list.filter((w) => w.status === k).length;
  const CONDS: { k: CondKey; label: string; n: number; hint: string }[] = [
    { k: "due", label: "Due soon", n: list.filter(isDueSoon).length,
      hint: "Delivery within 7 days, including anything already overdue. Complete jobs don't count." },
    { k: "staffing", label: "Open roles", n: list.filter(hasOpenRole).length,
      hint: "A proofer or reviewer on the job is still unfilled." },
  ];

  const all = statuses.size === 0;
  const hidingDone = !all && statuses.size === STATUS_ORDER.length - 1 && !statuses.has("complete");

  const toggleStatus = (k: StatusKey) => {
    // From "All", one click means "only this" — the tap the old tiles taught,
    // still doing what it did. After that the chips add and remove.
    if (all) return setStatuses(new Set([k]));
    const next = new Set(statuses);
    if (next.has(k)) next.delete(k); else next.add(k);
    // Every status ticked is the same view as none ticked; say it the short way.
    setStatuses(next.size === STATUS_ORDER.length ? new Set() : next);
  };
  // Unfiled is one of the options, not an absence of one: "the jobs nobody has
  // put anywhere" is a real thing to want to look at, and without it the row
  // could express every folder but not the gap between them.
  const nInFolder = (id: string) => list.filter((w) => folderOf(w, knownFolderIds) === id).length;
  const folderOpts = [...folders.map((f) => ({ id: f.id, name: f.name || "Untitled folder" })),
                      { id: UNFILED, name: "Unfiled" }];
  const allFolders = folderSel.size === 0;
  const toggleFolder = (id: string) => {
    // Same grammar as the status chips: from "all", one click narrows to this
    // one; after that they add and remove. Learning it once is the point.
    if (allFolders) return setFolderSel(new Set([id]));
    const next = new Set(folderSel);
    if (next.has(id)) next.delete(id); else next.add(id);
    setFolderSel(next.size === folderOpts.length ? new Set() : next);
  };

  const toggleCond = (k: CondKey) => {
    const next = new Set(conds);
    if (next.has(k)) next.delete(k); else next.add(k);
    setConds(next);
  };

  const filtering = !all || conds.size > 0 || !allFolders;
  return (
    <div className="wf-filters">
      <div className="wf-frow">
        <span className="wf-flbl">Show</span>
        <button className={`wf-chip wf-chip-all${all ? " on" : ""}`} aria-pressed={all}
          onClick={() => { setStatuses(new Set()); }}
          title="Every status, nothing hidden">All<span className="wf-chip-n">{list.length}</span></button>
        <span className="wf-fsep" aria-hidden="true" />
        {STATUS_ORDER.map((k) => {
          const m = STATUS[k], on = all || statuses.has(k), n = nFor(k);
          return (
            <button key={k} className={`wf-chip${statuses.has(k) ? " on" : ""}${n === 0 ? " empty" : ""}`}
              aria-pressed={statuses.has(k)} title={`${m.label} — ${m.hint}`}
              style={statuses.has(k) ? { background: m.bg, color: m.fg, borderColor: m.bg } : undefined}
              onClick={() => toggleStatus(k)}>
              <span className="wf-swatch" style={{ background: on ? m.dot : "var(--wf-line-strong)" }} />
              {m.label}<span className="wf-chip-n">{n}</span>
            </button>
          );
        })}
        <span className="wf-fsep" aria-hidden="true" />
        {/* The one exclusion worth a button of its own: complete is the only
            status that piles up forever, so "everything except done" is the
            view people actually keep. It's the same set of chips underneath. */}
        <button className={`wf-chip wf-chip-hide${hidingDone ? " on" : ""}`} aria-pressed={hidingDone}
          title="Everything except delivered jobs"
          onClick={() => setStatuses(hidingDone ? new Set() : new Set(STATUS_ORDER.filter((k) => k !== "complete")))}>
          {hidingDone ? "Complete hidden" : "Hide complete"}
        </button>
      </div>

      {/* Only when folders exist — an empty row teaching a feature nobody has
          used yet is just a line of noise above the board. */}
      {folders.length > 0 && (
        <div className="wf-frow">
          <span className="wf-flbl">Folder</span>
          <button className={`wf-chip wf-chip-all${allFolders ? " on" : ""}`} aria-pressed={allFolders}
            onClick={() => setFolderSel(new Set())}
            title="Every folder, and the jobs in none of them">All</button>
          <span className="wf-fsep" aria-hidden="true" />
          {folderOpts.map((f) => {
            const n = nInFolder(f.id), on = folderSel.has(f.id);
            return (
              <button key={f.id || "__unfiled"} className={`wf-chip wf-chip-folder${on ? " on" : ""}${n === 0 ? " empty" : ""}`}
                aria-pressed={on}
                title={f.id === UNFILED ? "Jobs that aren't in a folder" : `Only the jobs in ${f.name}`}
                onClick={() => toggleFolder(f.id)}>
                {f.name}<span className="wf-chip-n">{n}</span>
                {/* Unfiled is a place, not a folder — there is nothing to rename
                    or delete, so it gets no menu. */}
                {f.id !== UNFILED && (
                  <span className="wf-vx" role="button" tabIndex={0}
                    aria-label={`Rename or delete ${f.name}`} title="Rename or delete this folder"
                    onClick={(e) => { e.stopPropagation(); onFolderMenu(f.id, (e.currentTarget as HTMLElement).getBoundingClientRect()); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onFolderMenu(f.id, (e.currentTarget as HTMLElement).getBoundingClientRect()); } }}>⋯</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="wf-frow">
        <span className="wf-flbl">Display</span>
        {([
          { on: display.minimal, set: display.setMinimal, label: "Minimal",
            hint: "Denser rows — hides avatars, progress bars and source marks" },
          { on: display.notes, set: display.setNotes, label: "Notes",
            hint: "A free-text column beside Project. Hiding it never loses what you wrote" },
          { on: display.wide, set: display.setWide, label: "Full width",
            hint: "Let the grid use the whole window instead of the page's reading width" },
        ] as const).map((d) => (
          <button key={d.label} className={`wf-chip wf-chip-disp${d.on ? " on" : ""}`}
            aria-pressed={d.on} title={d.hint} onClick={() => d.set(!d.on)}>
            {d.label}
          </button>
        ))}
      </div>

      <div className="wf-frow">
        <span className="wf-flbl">Only</span>
        {CONDS.map((c) => (
          <button key={c.k} className={`wf-chip wf-chip-cond${conds.has(c.k) ? " on" : ""}${c.n === 0 ? " empty" : ""}`}
            aria-pressed={conds.has(c.k)} title={c.hint} onClick={() => toggleCond(c.k)}>
            {c.label}<span className="wf-chip-n">{c.n}</span>
          </button>
        ))}
        {/* Filters stick between visits, so the board must always be able to
            answer "why am I not seeing everything" without being asked. */}
        <span className={`wf-fsum${filtering ? " lit" : ""}`}>
          {filtering ? `Showing ${shown} of ${list.length}` : `${list.length} workflow${list.length === 1 ? "" : "s"}`}
          {filtering && (
            <button className="wf-fclear" onClick={() => { setStatuses(new Set()); setConds(new Set()); setFolderSel(new Set()); }}>Clear</button>
          )}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------
function Row({ w, cols, open, onToggleOpen, onOpenDrawer, onDragStart, onDragEnd, onPop, onLinkDo, onOpenProject, onKebab }: {
  w: Workflow; cols: Col[]; open: boolean;
  onToggleOpen: () => void; onOpenDrawer: () => void;
  onDragStart: () => void; onDragEnd: () => void;
  onPop: (p: Pop) => void; onLinkDo: (f: LinkField) => void;
  onOpenProject: (pid: string) => void; onKebab: (rect: DOMRect) => void;
}) {
  const rectOf = (e: ReactMouseEvent) => (e.currentTarget as HTMLElement).getBoundingClientRect();
  const stop = (e: ReactMouseEvent) => e.stopPropagation();
  return (
    <>
      {/* The row is a drag handle (move between statuses); it no longer opens the
          drawer — only the title does, and each field edits inline. */}
      <div className="wf-row" draggable style={{ gridTemplateColumns: "var(--wf-grid)" }}
        onDragStart={(e) => { onDragStart(); e.dataTransfer.effectAllowed = "move"; }}
        onDragEnd={onDragEnd}>
        {cols.map((c) => <Cell key={c.id} col={c.id} w={w} open={open} onToggleOpen={onToggleOpen} onOpenDrawer={onOpenDrawer} onPop={onPop} onLinkDo={onLinkDo} onOpenProject={onOpenProject} onKebab={onKebab} rectOf={rectOf} stop={stop} />)}
      </div>
      {/* Expanded: a linked row shows its live Tracking View (module × chapter
          matrix); an unlinked row with batches shows the batch sub-rows. */}
      {open && (w.project_id
        ? <TrackingView workflowId={w.id} />
        : w.batches?.map((b, i) => (
            <div key={i} className="wf-subrow" style={{ gridTemplateColumns: "var(--wf-grid)" }}>
              {cols.map((c) => <SubCell key={c.id} col={c.id} b={b} />)}
            </div>
          )))}
    </>
  );
}

function Cell({ col, w, open, onToggleOpen, onOpenDrawer, onPop, onLinkDo, onOpenProject, onKebab, rectOf, stop }: {
  col: ColId; w: Workflow; open: boolean; onToggleOpen: () => void; onOpenDrawer: () => void;
  onPop: (p: Pop) => void; onLinkDo: (f: LinkField) => void; onOpenProject: (pid: string) => void; onKebab: (rect: DOMRect) => void;
  rectOf: (e: ReactMouseEvent) => DOMRect; stop: (e: ReactMouseEvent) => void;
}) {
  const s = sumRt(w);
  const editableRt = !w.batches?.length; // batch runtime is the sum of its batches — read-only
  const rtPop = (e: ReactMouseEvent) => { if (editableRt) { stop(e); onPop({ kind: "runtime", id: w.id, rect: rectOf(e) }); } };
  const textCell = (field: TextField, cls: string, val: string, label: string, ph: string) => (
    <div className="wf-cell">
      <button className={`${cls} wf-ed${val ? "" : " empty"}`} draggable={false}
        onClick={(e) => { stop(e); onPop({ kind: "text", id: w.id, field, label, placeholder: ph, rect: rectOf(e) }); }}>
        {val || "Set"}
      </button>
    </div>
  );
  switch (col) {
    case "title": {
      return (
        <div className="wf-cell wf-cell-title">
          {/* Row menu leads the cell; the expander closes it on the right. The
              two swapped places so the control that opens the tracking view sits
              at the boundary with the rest of the row it expands into. */}
          <button className="wf-kebab" draggable={false} title="Rename · Archive · Delete" aria-label="Row menu" onClick={(e) => { stop(e); onKebab(rectOf(e)); }}>⋯</button>
          {/* The colour label, the same swatch and palette as an audio file's
              in the hub: a filled dot when set, a hollow ring when not, so an
              unlabelled row still shows where the label goes. */}
          <button type="button" className={`lbl-swatch${labelDot(w.label_color) ? " on" : ""}`} draggable={false}
            style={labelDot(w.label_color) ? { background: labelDot(w.label_color), borderColor: labelDot(w.label_color) } : undefined}
            title={`${labelName(w.label_color)} — click to change`} aria-label={`Label: ${labelName(w.label_color)}`}
            onClick={(e) => { stop(e); onPop({ kind: "label", id: w.id, rect: rectOf(e) }); }} />
          {/* A linked row's title goes straight to the project hub — the twisty
              already opens the tracking view, so the detail drawer is redundant.
              An unlinked tracker row has no hub, so it still opens the drawer. */}
          {w.project_id
            ? <span className="wf-tname wf-title-open" title="Open the project hub"
                onClick={(e) => { stop(e); onOpenProject(w.project_id as string); }}>{w.name || "Untitled"}</span>
            : <span className="wf-tname wf-title-open" onClick={onOpenDrawer} title="Open details">{w.name || "Untitled"}</span>}
          {w.subtitle && <span className="wf-tsub">· {w.subtitle}</span>}
          {/* The same M as the HH:MM:SS column, repeated beside the name.
              That column is eleventh of sixteen, so on any normal width it is
              off the right edge — the mark was there, and you had to go looking
              for it. Here it rides the part of the row that is always on
              screen, which is the only place "which of these are overridden?"
              can be answered by looking. */}
          {rtSource(w) === "manual" && (
            <span className="wf-mtag row" title={mTitle(w)}>M</span>
          )}
          {!!w.batches?.length && <BatchChip w={w} />}
          {w.project_id || w.batches?.length
            ? <button className={`wf-expand${open ? " open" : ""}`} draggable={false}
                onClick={(e) => { stop(e); onToggleOpen(); }}
                title={w.project_id ? "Show tracking view" : "Show batches"}
                aria-expanded={open}
                aria-label={w.project_id ? "Show tracking view" : "Show batches"}>
                <svg viewBox="0 0 10 10" width="13" height="13" aria-hidden="true">
                  <path d="M2 1 L8 5 L2 9 Z" fill="currentColor" />
                </svg>
              </button>
            : <span className="wf-expand spacer" />}
        </div>
      );
    }
    case "notes": {
      // The first line is the headline — a note that starts "Waiting on ch.4"
      // tells you what it is without being opened, and the rest is a tooltip
      // away. Whitespace-only counts as empty: a stray newline shouldn't make a
      // row look annotated.
      const text = (w.notes || "").trim();
      const first = text.split("\n")[0];
      return (
        <div className="wf-cell">
          <button className={`wf-note wf-ed${text ? "" : " empty"}`} draggable={false}
            title={text || "Add a note"}
            onClick={(e) => { stop(e); onPop({ kind: "notes", id: w.id, rect: rectOf(e) }); }}>
            {text ? <><span className="wf-note-t">{first}</span>{text.includes("\n") && <span className="wf-note-more">…</span>}</> : "Note"}
          </button>
        </div>
      );
    }
    case "service": return textCell("service", "wf-pill wf-svc", w.service, "Service", "e.g. 2nd QC");
    case "category": return textCell("category", "wf-cat", w.category, "Service category", "e.g. Audio Proofing");
    case "status": {
      const m = sMeta(w.status);
      return <div className="wf-cell"><button className="wf-pill wf-status" style={{ background: m.bg, color: m.fg }} title={`${m.label} — ${m.hint}`}
        draggable={false} onClick={(e) => { stop(e); onPop({ kind: "status", id: w.id, rect: rectOf(e) }); }}><span className="wf-dot" />{m.label}</button></div>;
    }
    case "proofer": return <RoleCell w={w} role="proofer" onPop={onPop} rectOf={rectOf} stop={stop} />;
    case "reviewer": return <RoleCell w={w} role="reviewer" onPop={onPop} rectOf={rectOf} stop={stop} />;
    case "client": return textCell("client", "wf-client", w.client, "Client", "Client name");
    case "hh": return <NumCell v={s ? Math.floor(s / 3600) : null} onClick={editableRt ? rtPop : undefined} />;
    case "mm": return <NumCell v={s ? pad2(Math.floor((s % 3600) / 60)) : null} onClick={editableRt ? rtPop : undefined} />;
    case "ss": return <NumCell v={s ? pad2(s % 60) : null} onClick={editableRt ? rtPop : undefined} />;
    case "hms": {
      const src = rtSource(w);
      return <div className={`wf-cell${editableRt ? " wf-ed-cell" : ""}`} onClick={rtPop} title={editableRt ? "Click to set runtime" : undefined}>{s ? <span className="wf-rt">{fmtRt(s)}</span> : <span className="wf-rt tbd">—</span>}
        {src && <span className={`wf-src ${src}`}
          title={src === "linked"
            ? "Measured from the audio files"
            // Naming the measured figure here is the whole point: the glyph alone
            // says "manual" but not what is being overridden, so there's nothing
            // to weigh the hand-typed number against without opening the row.
            : `${mTitle(w)}. Click to change or restore.`}>
          {src === "linked" ? "◈" : "M"}</span>}</div>;
    }
    case "frac": return <NumCell v={s ? (s / 3600).toFixed(2) : null} />;
    case "start": return <div className="wf-cell"><DateBtn w={w} field="start_date" onPop={onPop} rectOf={rectOf} stop={stop} /></div>;
    case "timeline": {
      if (!w.start_date || !w.delivery_date) return <div className="wf-cell"><span className="wf-tl-na">—</span></div>;
      const d = Math.round((+parseD(w.delivery_date) - +parseD(w.start_date)) / MS);
      return <div className="wf-cell"><span className={`wf-tl${d < 0 ? " neg" : ""}`} title="Days foreseen (delivery − start)">{d} d</span></div>;
    }
    case "delivery": return <div className="wf-cell"><div className="wf-dlwrap"><DateBtn w={w} field="delivery_date" onPop={onPop} rectOf={rectOf} stop={stop} /><DueChip w={w} /></div></div>;
    case "links": return <div className="wf-cell"><RefsCell w={w} onPop={onPop} onLinkDo={onLinkDo} rectOf={rectOf} stop={stop} /></div>;
  }
}

function SubCell({ col, b }: { col: ColId; b: WorkflowBatch }) {
  const m = sMeta(b.status);
  switch (col) {
    case "title": return <div className="wf-cell wf-cell-title"><span className="wf-batchtag">{b.tag}</span><span className="wf-tname sub">{b.name}</span></div>;
    case "status": return <div className="wf-cell"><span className="wf-pill wf-status" style={{ background: m.bg, color: m.fg, cursor: "default" }}><span className="wf-dot" />{m.label}</span></div>;
    case "proofer": return <div className="wf-cell">{b.who ? <span className="wf-av" style={{ background: avatarColor(b.who) }} title={b.who}>{initials(b.who)}</span> : <span className="wf-av empty">＋</span>}</div>;
    case "hh": return <NumCell v={b.rt ? Math.floor(b.rt / 3600) : null} />;
    case "mm": return <NumCell v={b.rt ? pad2(Math.floor((b.rt % 3600) / 60)) : null} />;
    case "ss": return <NumCell v={b.rt ? pad2(b.rt % 60) : null} />;
    case "hms": return <div className="wf-cell">{b.rt ? <span className="wf-rt">{fmtRt(b.rt)}</span> : <span className="wf-rt tbd">—</span>}</div>;
    case "frac": return <NumCell v={b.rt ? (b.rt / 3600).toFixed(2) : null} />;
    case "delivery": return <div className="wf-cell"><span className="wf-deliver">{fmtShort(b.delivery)}</span></div>;
    default: return <div className="wf-cell" />;
  }
}

function NumCell({ v, onClick }: { v: number | string | null; onClick?: (e: ReactMouseEvent) => void }) {
  const cls = `wf-cell wf-num${v == null ? " tbd" : ""}${onClick ? " wf-ed-cell" : ""}`;
  return <div className={cls} onClick={onClick} title={onClick ? "Click to set runtime" : undefined}>{v == null ? "—" : v}</div>;
}
function BatchChip({ w }: { w: Workflow }) {
  const done = w.batches.filter((b) => b.status === "complete").length;
  return <span className="wf-bchip" title={`${done} of ${w.batches.length} batches complete`}>
    <span className="wf-segbar">{w.batches.map((b, i) => <i key={i} style={{ background: sMeta(b.status).bar }} />)}</span>
    <small>{done}/{w.batches.length}</small>
  </span>;
}
function DueChip({ w }: { w: Workflow }) {
  if (w.status === "complete") return <span className="wf-due done">Done</span>;
  if (!w.delivery_date) return null;
  const d = Math.round((+parseD(w.delivery_date) - +today()) / MS);
  if (d < 0) return <span className="wf-due over">−{-d}d</span>;
  if (d === 0) return <span className="wf-due soon">Today</span>;
  if (d <= 4) return <span className="wf-due soon">{d}d</span>;
  return <span className="wf-due ok">{d}d</span>;
}
function DateBtn({ w, field, onPop, rectOf, stop }: { w: Workflow; field: "start_date" | "delivery_date"; onPop: (p: Pop) => void; rectOf: (e: ReactMouseEvent) => DOMRect; stop: (e: ReactMouseEvent) => void }) {
  const v = w[field] as string | null;
  return <button className={`wf-datebtn${v ? "" : " empty"}`} draggable={false} onClick={(e) => { stop(e); onPop({ kind: "cal", id: w.id, field, rect: rectOf(e) }); }}>{v ? fmtDMY(v) : "Set"}</button>;
}
// The proofer's own status, in the same words they see.
const ASSIGN_STATUS: Record<string, string> = {
  assigned: "To do", in_progress: "In progress", submitted: "Complete",
  delivered: "Delivered", invoiced: "Invoiced", settled: "Settled",
};

function RoleCell({ w, role, onPop, rectOf, stop }: { w: Workflow; role: "proofer" | "reviewer"; onPop: (p: Pop) => void; rectOf: (e: ReactMouseEvent) => DOMRect; stop: (e: ReactMouseEvent) => void }) {
  const who = w[role];
  const na = isNA(who);
  const openMenu = (e: ReactMouseEvent) => { stop(e); onPop({ kind: "people", id: w.id, role, rect: rectOf(e) }); };

  // A LINKED project's proofers are real assignments (they drive the proofer's
  // own "My Work"), so this column mirrors them instead of a hand-typed name —
  // and clicking opens the tracking view, where assigning actually happens. That
  // keeps ONE assign mechanism; a typed name here would never reach the proofer.
  if (role === "proofer" && w.project_id) {
    const assigned = w.assigned_proofers ?? [];
    // Proofing switched off for this project — same NA the Tech reviewer column
    // uses, same storage. Only meaningful while nobody is actually assigned.
    if (!assigned.length && na) {
      return (
        <div className="wf-cell wf-cell-na">
          <button className="wf-role napick" draggable={false}
            title="No proofer needed for this project — click to change"
            onClick={(e) => { stop(e); onPop({ kind: "assign", id: w.id, projectId: w.project_id!, rect: rectOf(e) }); }}>NA</button>
        </div>
      );
    }
    const tip = assigned.length
      ? `${assigned.map((a) => `${a.name}${a.scope_label ? ` · ${a.scope_label}` : " · whole project"} · ${ASSIGN_STATUS[a.status] ?? a.status}`).join("\n")}\n\nClick to assign, re-scope or unassign`
      : "Assign a proofer — whole project or selected files";
    // Assigning happens right here now, in the same popover the Tracking View
    // uses. The cell no longer has to expand the row to reach it.
    const openAssign = (e: ReactMouseEvent) => { stop(e); onPop({ kind: "assign", id: w.id, projectId: w.project_id!, rect: rectOf(e) }); };
    return (
      <div className="wf-cell">
        {assigned.length ? (
          <button className="wf-role filled wf-role-multi" draggable={false} title={tip} onClick={openAssign}>
            {assigned.slice(0, 3).map((a) => (
              <span key={a.assignment_id} className="wf-av" style={{ background: avatarColor(a.name) }}>{initials(a.name)}</span>
            ))}
            <span className="wf-nm">{assigned.length === 1 ? assigned[0].name : `${assigned.length} proofers`}</span>
          </button>
        ) : (
          <button className="wf-role-assign" draggable={false} title={tip} onClick={openAssign}>＋ Assign</button>
        )}
      </div>
    );
  }
  return (
    <div className={`wf-cell${na ? " wf-cell-na" : ""}`}>
      {na ? <button className="wf-role napick" draggable={false} title="Not required for this project — click to change" onClick={openMenu}>NA</button>
        : who ? <button className="wf-role filled" draggable={false} title={`${who} — click to reassign`} onClick={openMenu}><span className="wf-av" style={{ background: avatarColor(who) }}>{initials(who)}</span><span className="wf-nm">{who}</span></button>
          : <button className="wf-role-assign" draggable={false} onClick={openMenu}>＋ Assign</button>}
    </div>
  );
}
// The row's assets + links in ONE coherent strip, mirroring the project hub's
// Essentials: in canonical order (Manuscript · ToC · Review · Pass · Folder ·
// Script), every chip styled the same — one primary-action icon + label. The
// icon is the item's primary action (download a file, open a link, copy a
// password); clicking the rest of the chip is the secondary (open a file, or
// edit a link). Files are uploaded on the hub, not here.
type RefSlot = {
  key: string; label: string; icon: string; present: boolean;
  primary?: () => void;                      // icon action
  onSlot?: (e: ReactMouseEvent) => void;     // rest-of-chip action
  iconTip: string; slotTip: string;
};
function RefsCell({ w, onPop, onLinkDo, rectOf, stop }: { w: Workflow; onPop: (p: Pop) => void; onLinkDo: (f: LinkField) => void; rectOf: (e: ReactMouseEvent) => DOMRect; stop: (e: ReactMouseEvent) => void }) {
  const pid = w.project_id;
  const asset = (key: string, label: string, has: boolean, dl: string, open: string): RefSlot => ({
    key, label, icon: "download", present: !!(pid && has),
    iconTip: `Download the ${label.toLowerCase()}`,
    slotTip: pid && has ? `Open the ${label.toLowerCase()}` : `No ${label.toLowerCase()} yet`,
    primary: pid && has ? () => downloadFile(dl) : undefined,
    onSlot: pid && has ? () => window.open(open, "_blank", "noopener") : undefined,
  });
  const link = (f: LinkField): RefSlot => {
    const meta = LINK_META[f]; const present = !!(w[meta.col] as string);
    return {
      key: f, label: meta.label, icon: meta.icon, present,
      iconTip: meta.act, slotTip: (present ? "Edit " : "Add ") + meta.label,
      primary: present ? () => onLinkDo(f) : undefined,
      onSlot: (e) => onPop({ kind: "link", id: w.id, field: f, rect: rectOf(e) }),
    };
  };
  // Script isn't shown inline: the Manuscript essential is the narration script,
  // so a separate Script link here would be redundant. (Still editable in the
  // detail drawer for anyone who tracks a distinct script link.)
  const slots: RefSlot[] = [
    asset("manuscript", "Manuscript", w.has_manuscript, `/api/projects/${pid}/manuscript.pdf?download=1`, `/api/projects/${pid}/manuscript.pdf`),
    asset("toc", "ToC", w.has_toc, `/api/modules/toc/projects/${pid}/toc/file`, `/projects/${pid}/toc`),
    link("review"), link("pass"), link("folder"),
  ];
  return (
    <div className="wf-linkgrid">
      {slots.map((s) => (
        <span key={s.key} className={`wf-lslot ${s.present ? "on" : "off"}`} title={s.slotTip}
          onClick={s.onSlot ? (e) => { stop(e); s.onSlot!(e); } : undefined}>
          {s.present && s.primary
            ? <button className="wf-lsic" draggable={false} title={s.iconTip} onClick={(e) => { stop(e); s.primary!(); }}><Icon name={s.icon} size={11} /></button>
            : <span className="wf-lsic"><Icon name={s.icon} size={11} /></span>}
          <span className="wf-lslbl">{s.label}</span>
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Popover (status / people / calendar / link editor)
// ---------------------------------------------------------------------------
function Popover({ pop, wf, people, suggestions, onClose, onPatch }: { pop: Pop; wf: Workflow | undefined; people: string[]; suggestions: Suggestions; onClose: () => void; onPatch: (id: string, p: WorkflowPatch) => void }) {
  if (!pop || !wf) return null;
  // Every board popover routes through here, so this one line is what stops the
  // status menu on the last row opening past the bottom of the window.
  const style: CSSProperties = anchored(pop.rect, { width: 252 });

  if (pop.kind === "status") {
    return (
      <div className="wf-pop wf-menu" style={style}>
        <div className="wf-menu-h">Move to…</div>
        {STATUS_ORDER.map((k) => (
          <button key={k} title={STATUS[k].hint} onClick={() => { onPatch(wf.id, { status: k }); onClose(); }}>
            <span className="wf-mdot" style={{ background: STATUS[k].dot }} />{STATUS[k].label}
          </button>
        ))}
      </div>
    );
  }
  if (pop.kind === "label") {
    return (
      <div className="wf-pop wf-menu" style={anchored(pop.rect, { width: 182 })}>
        <div className="wf-menu-h">Label this row</div>
        {LABELS.map((l) => (
          <button key={l.key || "none"} className={(wf.label_color || "") === l.key ? "on" : undefined}
            onClick={() => { onPatch(wf.id, { label_color: l.key }); onClose(); }}>
            <span className={`lbl-swatch${l.dot ? " on" : ""}`}
              style={l.dot ? { background: l.dot, borderColor: l.dot, cursor: "inherit" } : { cursor: "inherit" }} />
            {l.name}
          </button>
        ))}
      </div>
    );
  }
  if (pop.kind === "assign") {
    return <AssignPop workflowId={pop.id} projectId={pop.projectId} name={wf.name || "Untitled"}
      na={{ value: isNA(wf.proofer), set: (on: boolean) => onPatch(wf.id, { proofer: on ? "NA" : null }) }}
      rect={pop.rect} onClose={onClose} />;
  }
  if (pop.kind === "people") {
    const role = pop.role; const cur = wf[role];
    return (
      <div className="wf-pop wf-menu" style={style}>
        <div className="wf-menu-h">Assign {role === "reviewer" ? "tech reviewer" : "proofer"}</div>
        <PeopleInput onPick={(name) => { onPatch(wf.id, set(role, name)); onClose(); }} />
        {people.map((p) => (
          <button key={p} onClick={() => { onPatch(wf.id, set(role, p)); onClose(); }}>
            <span className="wf-av sm" style={{ background: avatarColor(p) }}>{initials(p)}</span>{p}{cur === p ? " ·✓" : ""}
          </button>
        ))}
        <button className="wf-muted" onClick={() => { onPatch(wf.id, set(role, "NA")); onClose(); }}>
          <span className="wf-av sm empty">–</span>Not required (NA){isNA(cur) ? " ·✓" : ""}
        </button>
        {cur != null && !isNA(cur) && <button className="wf-muted" onClick={() => { onPatch(wf.id, set(role, null)); onClose(); }}>Unassign</button>}
      </div>
    );
  }
  if (pop.kind === "cal") {
    return <Calendar value={wf[pop.field] as string | null} style={style} onPick={(ds) => { onPatch(wf.id, set(pop.field, ds)); onClose(); }} />;
  }
  if (pop.kind === "text") {
    return <ComboEditor value={(wf[pop.field] as string) || ""} label={pop.label} placeholder={pop.placeholder} options={suggestions[pop.field]} style={style}
      onClose={onClose} onSave={(v) => { onPatch(wf.id, set(pop.field, v)); onClose(); }} />;
  }
  if (pop.kind === "runtime") {
    return <RuntimeEditor seconds={sumRt(wf) || 0} style={style} w={wf}
      onSave={(sec) => { onPatch(wf.id, { runtime_seconds: sec, runtime_source: "manual" }); onClose(); }}
      onUseMeasured={() => { onPatch(wf.id, { runtime_seconds: null, runtime_source: "linked" }); onClose(); }} />;
  }
  if (pop.kind === "notes") {
    return <NotesEditor value={wf.notes || ""} style={style} onClose={onClose}
      onSave={(v) => { onPatch(wf.id, { notes: v }); onClose(); }} />;
  }
  if (pop.kind === "link") {
    return <LinkEditor wf={wf} field={pop.field} style={style} onClose={onClose} onPatch={onPatch} />;
  }
  return null;
}

// Inline combobox editor (service / category / client). Type a new value (Enter
// to add it) or pick an existing one — the option list is every distinct value
// already used, so it builds up as records are added.
function ComboEditor({ value, label, placeholder, options, style, onClose, onSave }: { value: string; label: string; placeholder: string; options: string[]; style: CSSProperties; onClose: () => void; onSave: (v: string) => void }) {
  const [v, setV] = useState(value);
  const inp = useRef<HTMLInputElement>(null);
  useEffect(() => { inp.current?.focus(); inp.current?.select(); }, []);
  const q = v.trim().toLowerCase();
  const untouched = v.trim() === value.trim();  // still showing the pre-filled value → list all
  const filtered = options.filter((o) => o.trim() !== value.trim() && (untouched || o.toLowerCase().includes(q))).slice(0, 12);
  return (
    <div className="wf-pop wf-linkpop wf-combo" style={style}>
      <div className="wf-menu-h">{label}</div>
      <div className="wf-lp-row">
        <input ref={inp} className="wf-inp" placeholder={placeholder} value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSave(v.trim()); } else if (e.key === "Escape") { e.preventDefault(); onClose(); } }} />
        <button className="wf-lp-save" onClick={() => onSave(v.trim())} title="Save (Enter)">✓</button>
      </div>
      {filtered.length > 0 && (
        <div className="wf-combo-list">
          {filtered.map((o) => <button key={o} className="wf-combo-opt" onClick={() => onSave(o)}>{o}</button>)}
        </div>
      )}
    </div>
  );
}

/** A note is a paragraph, so it gets a textarea rather than the one-line combo
 *  the other text cells use. Saves on blur and on Cmd/Ctrl+Enter, not on every
 *  keystroke: this is prose, and a PATCH per character would be a PATCH per
 *  character. Escape abandons the edit, which is the only way to take back a
 *  sentence you have started regretting halfway through. */
function NotesEditor({ value, style, onSave, onClose }: {
  value: string; style: CSSProperties; onSave: (v: string) => void; onClose: () => void;
}) {
  const [v, setV] = useState(value);
  const done = () => { if (v !== value) onSave(v); else onClose(); };
  return (
    <div className="wf-pop wf-linkpop wf-notepop" style={style}>
      <div className="wf-menu-h">Note</div>
      <textarea className="wf-inp wf-notearea" autoFocus value={v} rows={5}
        placeholder="What's going on with this one…"
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") { e.preventDefault(); onClose(); }
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); done(); }
        }} />
      <div className="wf-note-foot">
        <span className="wf-note-hint">⌘↵ to save · Esc to discard</span>
        <button className="wf-lp-save" onClick={done} title="Save">✓</button>
      </div>
    </div>
  );
}

// Inline runtime editor (HH / MM / SS → total seconds).
function RuntimeEditor({ seconds, style, w, onSave, onUseMeasured }: {
  seconds: number; style: CSSProperties; w: Workflow;
  onSave: (sec: number) => void; onUseMeasured: () => void;
}) {
  const [h, setH] = useState(Math.floor(seconds / 3600));
  const [m, setM] = useState(Math.floor((seconds % 3600) / 60));
  const [s, setS] = useState(seconds % 60);
  const save = () => onSave(Math.max(0, h) * 3600 + Math.max(0, m) * 60 + Math.max(0, s));
  return (
    <div className="wf-pop wf-linkpop" style={style}>
      <div className="wf-menu-h">Runtime</div>
      <div className="wf-rt-edit">
        <label><span>HH</span><input className="wf-inp" type="number" min={0} value={h} autoFocus onChange={(e) => setH(+e.target.value || 0)} /></label>
        <label><span>MM</span><input className="wf-inp" type="number" min={0} max={59} value={m} onChange={(e) => setM(+e.target.value || 0)} /></label>
        <label><span>SS</span><input className="wf-inp" type="number" min={0} max={59} value={s} onChange={(e) => setS(+e.target.value || 0)} /></label>
        <button className="wf-lp-save" onClick={save} title="Save">✓</button>
      </div>
      <div className="wf-rt-foot"><RuntimeSource w={w} onUseMeasured={onUseMeasured} /></div>
    </div>
  );
}

function PeopleInput({ onPick }: { onPick: (name: string) => void }) {
  const [v, setV] = useState("");
  return (
    <div className="wf-menu-row">
      <input className="wf-inp" autoFocus placeholder="Type a name…" value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && v.trim()) { e.preventDefault(); onPick(v.trim()); } }} />
      <button className="wf-mini" onClick={() => { if (v.trim()) onPick(v.trim()); }} title="Add">✓</button>
    </div>
  );
}


/** The Proofer cell's popover: the SAME assignment panel the Tracking View
 *  shows, so there's one way to do this rather than two. The file list is
 *  fetched only once the popover opens (`useTracking`'s enabled flag) — the
 *  board's own list request must stay free of per-row I/O. */
function AssignPop({ workflowId, projectId, name, na, rect, onClose }: {
  workflowId: string; projectId: string; name: string;
  na: { value: boolean; set: (on: boolean) => void };
  rect: DOMRect; onClose: () => void;
}) {
  const { view, isLoading } = useTracking(workflowId, true);
  const files = view?.files ?? [];

  // The panel is tall, so it can't just hang off the row: flip above when
  // there's more room there, and cap the height so it always fits the viewport.
  const W = 384, PAD = 14;
  const below = window.innerHeight - rect.bottom - PAD;
  const above = rect.top - PAD;
  const goDown = below >= 360 || below >= above;
  const style: CSSProperties = {
    position: "fixed",
    left: Math.max(8, Math.min(rect.left, window.innerWidth - W - 8)),
    maxHeight: Math.max(260, Math.min(660, goDown ? below : above)),
    ...(goDown ? { top: rect.bottom + 6 } : { bottom: window.innerHeight - rect.top + 6 }),
  };

  return (
    <div className="wf-pop ap ap-pop" style={style} role="dialog" aria-label="Assign proofer">
      <div className="ap-pop-h">
        <span className="t">Assign proofer</span>
        <span className="proj" title={name}>{name}</span>
      </div>
      <div className="ap-pop-body">
        {isLoading && !files.length
          ? <div className="ap-sec muted">Loading files…</div>
          : <AssignProoferPanel projectId={projectId} files={files} na={na} />}
      </div>
      <div className="ap-pop-f">
        <button className="ap-link" onClick={onClose}>Done</button>
        <span className="esc">Esc to close</span>
      </div>
    </div>
  );
}

function Calendar({ value, style, onPick }: { value: string | null; style: CSSProperties; onPick: (ds: string) => void }) {
  const cur = value ? parseD(value) : null;
  const wd = ["M", "T", "W", "T", "F", "S", "S"];
  const months: ReactElement[] = [];
  const scrollRef = useRef<HTMLDivElement>(null);
  const curYm = cur ? `${cur.getFullYear()}-${pad2(cur.getMonth() + 1)}` : `${today().getFullYear()}-${pad2(today().getMonth() + 1)}`;
  for (let i = -2; i <= 13; i++) {
    const m = new Date(today().getFullYear(), today().getMonth() + i, 1);
    const y = m.getFullYear(), mo = m.getMonth();
    const offset = (new Date(y, mo, 1).getDay() + 6) % 7;
    const days = new Date(y, mo + 1, 0).getDate();
    const ym = `${y}-${pad2(mo + 1)}`;
    const cells: ReactElement[] = [];
    for (let k = 0; k < offset; k++) cells.push(<span key={`b${k}`} className="wf-cal-d blank" />);
    for (let dn = 1; dn <= days; dn++) {
      const ds = `${y}-${pad2(mo + 1)}-${pad2(dn)}`;
      const cls = `wf-cal-d${ds === ymd(today()) ? " today" : ""}${cur && ds === ymd(cur) ? " sel" : ""}`;
      cells.push(<button key={dn} className={cls} onClick={() => onPick(ds)}>{dn}</button>);
    }
    months.push(<div key={ym} className={`wf-cal-m${ym === curYm ? " cur" : ""}`} data-cur={ym === curYm ? "1" : undefined}>
      <div className="wf-cal-mt">{m.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</div>
      <div className="wf-cal-grid">{cells}</div>
    </div>);
  }
  useEffect(() => { const el = scrollRef.current?.querySelector<HTMLElement>(".wf-cal-m.cur"); if (el && scrollRef.current) scrollRef.current.scrollTop = el.offsetTop; }, []);
  return (
    <div className="wf-pop wf-cal" style={style}>
      <div className="wf-cal-h">{wd.map((d, i) => <span key={i}>{d}</span>)}</div>
      <div className="wf-cal-scroll" ref={scrollRef}>{months}</div>
    </div>
  );
}

function LinkEditor({ wf, field, style, onClose, onPatch }: { wf: Workflow; field: LinkField; style: CSSProperties; onClose: () => void; onPatch: (id: string, p: WorkflowPatch) => void }) {
  const meta = LINK_META[field]; const isPass = field === "pass";
  const cur = (wf[meta.col] as string) || "";
  const [v, setV] = useState(cur);
  const inp = useRef<HTMLInputElement>(null);
  useEffect(() => { inp.current?.focus(); inp.current?.select(); }, []);
  const target = (val: string): LinkField => { if (isPass || cur) return field; const t = hostRoute(val.trim()); return t && t !== field ? t : field; };
  const routed = target(v);
  const save = () => {
    const val = v.trim(); const t = target(val);
    onPatch(wf.id, set(LINK_META[t].col, val));
    onClose();
  };
  return (
    <div className="wf-pop wf-linkpop" style={style}>
      <div className="wf-menu-h">{meta.label}</div>
      <div className="wf-lp-row">
        <input ref={inp} className="wf-inp" spellCheck={false} placeholder={isPass ? "Enter or paste the password" : "Paste link…"} value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } else if (e.key === "Escape") { e.preventDefault(); onClose(); } }} />
        <button className="wf-lp-save" onClick={save} title="Save (Enter)">✓</button>
      </div>
      {!isPass && routed !== field && <div className="wf-lp-hint">Looks like a <b>{LINK_META[routed].label}</b> link — it’ll file there.</div>}
      {cur && (
        <div className="wf-lp-actions">
          {isPass
            ? <button className="wf-lp-act" onClick={() => { navigator.clipboard?.writeText(cur).catch(() => {}); }}>Copy</button>
            : <button className="wf-lp-act" onClick={() => openUrl(cur)}>Open ↗</button>}
          <button className="wf-lp-act danger" onClick={() => { onPatch(wf.id, set(meta.col, "")); onClose(); }}>Remove</button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer
// ---------------------------------------------------------------------------
function Drawer({ wf, linkedProject, suggestions, onClose, onPatch, onDelete, onPop, onOpenProject }: { wf: Workflow; linkedProject?: Project; suggestions: Suggestions; onClose: () => void; onPatch: (id: string, p: WorkflowPatch) => void; onDelete: () => void; onPop: (p: Pop) => void; onOpenProject: (pid: string) => void }) {
  // Local drafts for text fields — commit on blur (avoids a PATCH per keystroke).
  const [draft, setDraft] = useState({ name: wf.name, subtitle: wf.subtitle, service: wf.service, category: wf.category, client: wf.client });
  useEffect(() => { setDraft({ name: wf.name, subtitle: wf.subtitle, service: wf.service, category: wf.category, client: wf.client }); }, [wf.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = (field: keyof typeof draft) => { if (draft[field] !== (wf[field] as string)) onPatch(wf.id, set(field, draft[field])); };
  const setField = (field: keyof typeof draft, val: string) => setDraft((d) => ({ ...d, [field]: val }));

  const rt = sumRt(wf) || 0;
  const [rh, rm, rs] = [Math.floor(rt / 3600), Math.floor((rt % 3600) / 60), rt % 60];
  const setRuntime = (h: number, m: number, s: number) => onPatch(wf.id, { runtime_seconds: h * 3600 + m * 60 + s, runtime_source: "manual" });
  // null, not 0: the column is nullable and `sumRt` falls through on empty, so
  // this hands the row back to the audio files rather than pinning it to zero.
  const useMeasured = () => onPatch(wf.id, { runtime_seconds: null, runtime_source: "linked" });
  const m = sMeta(wf.status);
  const rectOf = (e: ReactMouseEvent) => (e.currentTarget as HTMLElement).getBoundingClientRect();

  const roleRow = (role: "proofer" | "reviewer", label: string) => {
    const who = wf[role];
    return (
      <div className="wf-role-row">
        <span className="wf-rl">{label}</span>
        <button className="wf-assignee" onClick={(e) => onPop({ kind: "people", id: wf.id, role, rect: rectOf(e) })}>
          {isNA(who) ? <><span className="wf-av sm empty">–</span><span className="wf-muted-t">Not required</span></>
            : who ? <><span className="wf-av sm" style={{ background: avatarColor(who) }}>{initials(who)}</span><span>{who}</span></>
              : <><span className="wf-av sm empty">＋</span><span className="wf-assign-t">Assign…</span></>}
        </button>
      </div>
    );
  };
  const linkRow = (field: LinkField, label: string) => {
    const meta = LINK_META[field]; const val = (wf[meta.col] as string) || "";
    return (
      <div className="wf-link-row">
        <span className="wf-ll"><Icon name={meta.icon} size={13} /><span className="wf-ll-nm">{label}</span></span>
        {val
          ? <span className="wf-ll-val" title={val}>{val}</span>
          : <span className="wf-muted-t">Not linked</span>}
        <button className="btn sm ghost" onClick={(e) => onPop({ kind: "link", id: wf.id, field, rect: rectOf(e) })}>{val ? "Edit" : "Add"}</button>
      </div>
    );
  };

  return (
    <aside className="wf-drawer on" aria-label="Workflow detail">
      <div className="wf-dw-head">
        <button className="btn ghost sm wf-dw-close" onClick={onClose} aria-label="Close">✕</button>
        <div className="wf-dw-eyebrow">
          {wf.service && <span className="wf-pill wf-svc">{wf.service}</span>}
          <button className="wf-pill wf-status" style={{ background: m.bg, color: m.fg }} onClick={(e) => onPop({ kind: "status", id: wf.id, rect: rectOf(e) })}><span className="wf-dot" />{m.label}</button>
        </div>
        <div className="wf-dw-title">{wf.name || "Untitled"}{wf.subtitle && <span className="wf-dw-titlesub"> · {wf.subtitle}</span>}</div>
      </div>
      <div className="wf-dw-body">
        <section className="wf-dw-sec">
          <h4>Linked project</h4>
          {wf.project_id ? (
            <div className="wf-link-row" style={{ borderTop: "none", paddingTop: 0 }}>
              <span className="wf-ll"><Icon name="folder" size={13} /><span className="wf-ll-nm">{linkedProject?.name ?? "Project"}</span></span>
              <button className="btn sm ghost" onClick={() => onOpenProject(wf.project_id as string)}>Open</button>
              <button className="btn sm ghost" onClick={() => onPatch(wf.id, { project_id: null })}>Unlink</button>
            </div>
          ) : (
            <div className="wf-muted-t">Not linked to a project — a standalone tracker row. Use “Add projects” on the board to pull one in.</div>
          )}
        </section>
        <section className="wf-dw-sec">
          <h4>Details</h4>
          {/* datalists give the same self-building dropdowns as the grid combos */}
          <datalist id="wf-dl-service">{suggestions.service.map((o) => <option key={o} value={o} />)}</datalist>
          <datalist id="wf-dl-category">{suggestions.category.map((o) => <option key={o} value={o} />)}</datalist>
          <datalist id="wf-dl-client">{suggestions.client.map((o) => <option key={o} value={o} />)}</datalist>
          <label className="wf-fld"><span>Name</span><input className="wf-inp" value={draft.name} onChange={(e) => setField("name", e.target.value)} onBlur={() => commit("name")} /></label>
          <label className="wf-fld"><span>Subtitle</span><input className="wf-inp" value={draft.subtitle} onChange={(e) => setField("subtitle", e.target.value)} onBlur={() => commit("subtitle")} /></label>
          <div className="wf-fld-2">
            <label className="wf-fld"><span>Service</span><input className="wf-inp" list="wf-dl-service" value={draft.service} onChange={(e) => setField("service", e.target.value)} onBlur={() => commit("service")} placeholder="e.g. 2nd QC" /></label>
            <label className="wf-fld"><span>Service category</span><input className="wf-inp" list="wf-dl-category" value={draft.category} onChange={(e) => setField("category", e.target.value)} onBlur={() => commit("category")} placeholder="e.g. Audio Proofing" /></label>
          </div>
          <label className="wf-fld"><span>Client</span><input className="wf-inp" list="wf-dl-client" value={draft.client} onChange={(e) => setField("client", e.target.value)} onBlur={() => commit("client")} /></label>
        </section>
        <section className="wf-dw-sec">
          <h4>Assignment</h4>
          {roleRow("proofer", "Proofer")}
          {roleRow("reviewer", "Tech reviewer")}
        </section>
        <section className="wf-dw-sec">
          <h4>Schedule</h4>
          <div className="wf-fld-2">
            <div className="wf-fld"><span>Start date</span><button className="wf-inp wf-datein" onClick={(e) => onPop({ kind: "cal", id: wf.id, field: "start_date", rect: rectOf(e) })}>{wf.start_date ? fmtDMY(wf.start_date) : "Set date…"}</button></div>
            <div className="wf-fld"><span>Delivery date</span><button className="wf-inp wf-datein" onClick={(e) => onPop({ kind: "cal", id: wf.id, field: "delivery_date", rect: rectOf(e) })}>{wf.delivery_date ? fmtDMY(wf.delivery_date) : "Set date…"}</button></div>
          </div>
        </section>
        <section className="wf-dw-sec">
          <h4>Runtime</h4>
          <div className="wf-rt-grid">
            <label><span>HH</span><input className="wf-inp" type="number" min={0} defaultValue={rh} key={`h${rt}`} onBlur={(e) => setRuntime(+e.target.value || 0, rm, rs)} /></label>
            <label><span>MM</span><input className="wf-inp" type="number" min={0} max={59} defaultValue={rm} key={`m${rt}`} onBlur={(e) => setRuntime(rh, +e.target.value || 0, rs)} /></label>
            <label><span>SS</span><input className="wf-inp" type="number" min={0} max={59} defaultValue={rs} key={`s${rt}`} onBlur={(e) => setRuntime(rh, rm, +e.target.value || 0)} /></label>
            <label><span>Frac (h)</span><input className="wf-inp" value={(rt / 3600).toFixed(2)} readOnly /></label>
          </div>
          <RuntimeSource w={wf} onUseMeasured={useMeasured} />
        </section>
        <section className="wf-dw-sec">
          <h4>Links &amp; access</h4>
          {linkRow("script", "Script")}
          {linkRow("review", "Review")}
          {linkRow("pass", "Review password")}
          {linkRow("folder", "Folder")}
        </section>
        <section className="wf-dw-sec">
          <button className="btn ghost sm wf-danger" onClick={onDelete}>Delete workflow</button>
        </section>
      </div>
    </aside>
  );
}
